// src/pages/demo/DemoHomeworkScheduleDetail.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { homeworkScheduleAPI, homeworkAPI, studentAPI, lessonPlansAPI, studentTutorialLinkAPI, studentTutorialAPI, translationAPI, lessonAnalysisAPI, contentLibraryAPI } from '../../services/api';
import { trackUserEvent } from '../../utils/telemetry';
import HomeworkScheduleGuideOverlay from '../../components/HomeworkScheduleGuideOverlay';
import { hasShownThisSession, isProgressiveGuideLoginEligible, markShownThisSession, setGuideUser } from '../../utils/guideSession';
import { usesCustomUploadPortal } from '../../utils/uploadPortal';

// --- BRAND PALETTE ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F',
    success: '#81C784'
};

const toTitleCaseTag = (value = '') => value.toString().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

const extractLessonNumberFromTag = (tag = '') => {
    const match = String(tag || '').match(/\blesson\s*(\d+)\b/i);
    const value = match ? Number(match[1]) : 0;
    return Number.isFinite(value) && value > 0 ? value : 0;
};

const getMaxLessonNumber = (...collections) => collections
    .flat()
    .reduce((max, value) => Math.max(max, extractLessonNumberFromTag(value)), 0);

const normalizeRegistrationCode = (code) => {
    if (!code) return '';
    return code.toString().trim().replace(/\s+/g, ' ').toUpperCase();
};

const stripLessonPrefix = (text = '', lessonTag = '') => {
    if (!text) return '';
    const escaped = lessonTag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(new RegExp(`^${escaped}\\s*:\\s*`, 'i'), '').trim();
};

const deriveLessonTopicFromEntry = (entry = {}, lessonTag = '') => {
    const vocab = Array.isArray(entry?.tier3Vocabulary) ? entry.tier3Vocabulary[0] : '';
    const vocabText = (vocab || '').toString().split(':')[0].trim();
    return stripLessonPrefix(vocabText, lessonTag);
};

const sanitizeWorksheetText = (value = '') => String(value || '')
    .replace(/[*_`#>]/g, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const escapeWorksheetHtml = (value = '') => sanitizeWorksheetText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getWorksheetTerm = (value = '') => {
    const cleaned = sanitizeWorksheetText(value);
    if (!cleaned) return '';
    return cleaned.split(/\s*[:\-–—]\s*/)[0].trim();
};

const collectWorksheetVocabulary = (entries = [], fallbackTopic = '') => {
    const seen = new Set();
    const terms = [];
    const addTerm = (value) => {
        const term = getWorksheetTerm(value);
        const key = term.toLowerCase();
        if (!term || seen.has(key)) return;
        seen.add(key);
        terms.push(term);
    };

    entries.forEach(({ entry, topic }) => {
        (Array.isArray(entry?.tier3Vocabulary) ? entry.tier3Vocabulary : []).forEach(addTerm);
        (Array.isArray(entry?.objectives) ? entry.objectives : []).forEach(item => {
            const objective = sanitizeWorksheetText(item?.objective);
            const match = objective.match(/\b([A-Z][a-z]{4,}(?:\s+[A-Z][a-z]{4,})?)\b/);
            if (match) addTerm(match[1]);
        });
        addTerm(topic);
    });

    if (terms.length < 1) addTerm(fallbackTopic);
    if (terms.length < 2) addTerm('Key Word');
    return terms.slice(0, 2);
};

const getWorksheetTopic = (entry = {}, lessonTag = '', lessonTitle = '') => {
    const titleWithoutTag = stripLessonPrefix(sanitizeWorksheetText(lessonTitle), lessonTag);
    const derivedTopic = deriveLessonTopicFromEntry(entry, lessonTag);
    return titleWithoutTag || derivedTopic || sanitizeWorksheetText(entry?.title) || lessonTag || 'this topic';
};

const getWorksheetSortingData = (entries = [], topic = '', vocabTerms = []) => {
    const categorisedEntry = entries.find(({ entry }) => (
        entry?.categorisation
        && !entry.categorisation.isNA
        && Array.isArray(entry.categorisation.itemsA)
        && entry.categorisation.itemsA.length > 0
    ));

    if (categorisedEntry) {
        const categorisation = categorisedEntry.entry.categorisation;
        const leftItems = categorisation.itemsA.slice(0, 2).map(sanitizeWorksheetText).filter(Boolean);
        const rightItems = (Array.isArray(categorisation.itemsB) ? categorisation.itemsB : []).slice(0, 2).map(sanitizeWorksheetText).filter(Boolean);
        return {
            leftLabel: sanitizeWorksheetText(categorisation.categoryA) || 'Group 1',
            rightLabel: sanitizeWorksheetText(categorisation.categoryB) || 'Group 2',
            items: [...leftItems, ...rightItems].slice(0, 4)
        };
    }

    const topicItems = entries.map(({ topic: lessonTopic }) => lessonTopic).filter(Boolean);
    return {
        leftLabel: 'Lesson ideas',
        rightLabel: 'Everyday examples',
        items: [...topicItems, ...vocabTerms, 'Mobile phone', 'Laptop'].filter(Boolean).slice(0, 4)
    };
};

const getWorksheetObjective = (entries = [], topic = '') => {
    const objectiveEntry = entries.find(({ entry }) => Array.isArray(entry?.objectives) && entry.objectives[0]?.objective);
    return objectiveEntry
        ? sanitizeWorksheetText(objectiveEntry.entry.objectives[0].objective)
        : `Show one thing you know about ${topic}.`;
};

const buildPrePostWorksheet = ({ testType = 'Pre-Test', lessons = [], moduleTitle = '' }) => {
    const safeLessons = lessons.length > 0 ? lessons : [{ lessonTag: 'All Lessons', entry: {}, topic: moduleTitle || 'this topic' }];
    const topic = sanitizeWorksheetText(moduleTitle) || safeLessons.map(lesson => lesson.topic).filter(Boolean).slice(0, 3).join(' / ') || 'this topic';
    const lessonRange = safeLessons.length === 1 ? safeLessons[0].lessonTag : `${safeLessons.length} lessons`;
    const vocabTerms = collectWorksheetVocabulary(safeLessons, topic);
    const sorting = getWorksheetSortingData(safeLessons, topic, vocabTerms);
    const objective = getWorksheetObjective(safeLessons, topic);
    const lessonTopics = safeLessons.map(lesson => lesson.topic).filter(Boolean);

    return {
        testType,
        lessonTag: lessonRange,
        topic,
        vocabTerms,
        sorting,
        objective,
        lessonTopics,
        title: `${testType}: ${topic}`
    };
};

const mapAssessmentImagesById = (images = []) => images.reduce((acc, image) => {
    if (image?.id && image?.imageUrl) acc[image.id] = image.imageUrl;
    return acc;
}, {});

const normalizeGeneratedAssessment = ({ testType = 'Pre-Test', assessment = {}, images = [] }) => {
    const imageById = mapAssessmentImagesById(images);
    const visualChoices = Array.isArray(assessment?.pages?.visualRecognition?.choices)
        ? assessment.pages.visualRecognition.choices.slice(0, 3)
        : [];
    const vocabularyItems = Array.isArray(assessment?.pages?.vocabulary?.items)
        ? assessment.pages.vocabulary.items.slice(0, 4)
        : [];
    const sortingItems = Array.isArray(assessment?.pages?.sorting?.items)
        ? assessment.pages.sorting.items.slice(0, 4)
        : [];

    return {
        testType,
        lessonTag: assessment?.coverageSummary || 'All lessons',
        topic: sanitizeWorksheetText(assessment?.topic || assessment?.title || 'this topic'),
        title: sanitizeWorksheetText(assessment?.title || `${testType}: ${assessment?.topic || 'All lessons'}`),
        coverageSummary: sanitizeWorksheetText(assessment?.coverageSummary || ''),
        vocabTerms: vocabularyItems.map(item => sanitizeWorksheetText(item?.word)).filter(Boolean),
        lessonTopics: [sanitizeWorksheetText(assessment?.topic || assessment?.title || 'All lessons')].filter(Boolean),
        visualRecognition: {
            question: sanitizeWorksheetText(assessment?.pages?.visualRecognition?.question || `Which one is ${assessment?.topic || 'this topic'}?`),
            choices: visualChoices.map((choice, index) => ({
                id: choice?.id || `vr${index + 1}`,
                label: sanitizeWorksheetText(choice?.label || `Choice ${index + 1}`),
                isCorrect: choice?.isCorrect === true,
                imageUrl: imageById[choice?.id]
            }))
        },
        vocabulary: {
            items: vocabularyItems.map((item, index) => ({
                id: item?.id || `vocab${index + 1}`,
                word: sanitizeWorksheetText(item?.word || `Key Word ${index + 1}`)
            }))
        },
        sorting: {
            question: sanitizeWorksheetText(assessment?.pages?.sorting?.question || 'Match the items to where they belong.'),
            leftLabel: sanitizeWorksheetText(assessment?.pages?.sorting?.leftLabel || 'Group 1'),
            rightLabel: sanitizeWorksheetText(assessment?.pages?.sorting?.rightLabel || 'Group 2'),
            items: sortingItems.map(item => sanitizeWorksheetText(item?.label)).filter(Boolean)
        },
        questions: Array.isArray(assessment?.pages?.knowledge?.questions)
            ? assessment.pages.knowledge.questions.slice(0, 4).map(q => sanitizeWorksheetText(q?.question || '')).filter(Boolean)
            : [
                sanitizeWorksheetText(assessment?.pages?.knowledge?.question1 || `Show one thing you know about ${assessment?.topic || 'this topic'}.`),
                sanitizeWorksheetText(assessment?.pages?.knowledge?.question2 || `Did this lesson include ${assessment?.topic || 'this topic'}?`)
            ],
        generatedAssessment: assessment
    };
};

const getPrePostGenerationMessage = (err) => {
    if (err?.response?.status === 409) {
        return 'Lesson analysis is still being prepared. Please wait for lesson analysis to finish, then try again.';
    }
    if (err?.response?.status === 404) {
        return 'We could not find this lesson plan. Please refresh the page and try again.';
    }
    if (err?.response?.status === 401) {
        return 'Your session needs refreshing. Please log in again, then try generating the worksheet.';
    }
    return 'We could not generate the worksheet just now. Please try again in a moment.';
};

const renderTeacherDataStrip = ({ scoreLabel = 'Task Score: ___ / 2', nnaw = 'Reader ⬜ Extra Time ⬜ Visuals ⬜ Laptop' } = {}) => `
    <div class="teacher-strip">
        <div class="strip-row"><strong>${scoreLabel}</strong><span>|</span><strong>EHCP Prompt Level:</strong> ⬜ 1 (Full Phys) ⬜ 2 (Gestural) ⬜ 3 (Verbal) ⬜ 4 (Visual) ⬜ 5 (Indep)</div>
        <div class="strip-row"><strong>DNE (Refused):</strong> ⬜<span>|</span><strong>NNoW Used:</strong> ⬜ ${nnaw}</div>
    </div>
`;

const renderWorksheetImage = (imageUrl, fallbackSymbol, altText, className = 'worksheet-image') => imageUrl
    ? `<img class="${className}" src="${imageUrl}" alt="${escapeWorksheetHtml(altText)}" />`
    : `<div class="visual-symbol">${fallbackSymbol}</div>`;

const renderVisualChoice = (choice, symbol, index) => `
    <div class="visual-choice">
        ${renderWorksheetImage(choice?.imageUrl, symbol, choice?.label || `Choice ${index}`)}
        <div class="tick-box">☐</div>
        <div class="choice-number">${index}</div>
    </div>
`;

const createWorksheetPrintHtml = (worksheet, students = []) => {
    const visualChoices = worksheet.visualRecognition?.choices?.length >= 3
        ? worksheet.visualRecognition.choices
        : [
            { label: 'Different thing' },
            { label: worksheet.topic },
            { label: 'Modern car' }
        ];
    const vocabItems = worksheet.vocabulary?.items?.length >= 4
        ? worksheet.vocabulary.items
        : [
            { word: worksheet.vocabTerms?.[0] || worksheet.topic },
            { word: worksheet.vocabTerms?.[1] || 'Key Word 2' },
            { word: worksheet.vocabTerms?.[2] || 'Key Word 3' },
            { word: worksheet.vocabTerms?.[3] || 'Key Word 4' }
        ];
    const sortingItems = worksheet.sorting.items.length > 0 ? worksheet.sorting.items : [worksheet.topic, 'Mobile phone', 'Laptop', 'Picture'];
    const visualQuestion = escapeWorksheetHtml(worksheet.visualRecognition?.question || `Which one is ${worksheet.topic}?`);
    const sortingQuestion = escapeWorksheetHtml(worksheet.sorting?.question || 'Match the items to where they belong.');
    const knowledgeQuestions = Array.isArray(worksheet.questions) && worksheet.questions.length > 0
        ? worksheet.questions
        : [
            `Show one thing you know about ${worksheet.topic}.`,
            `Did this lesson include ${worksheet.topic}?`,
            `Is ${worksheet.topic} a key topic in this lesson?`,
            `Can you name one fact from this lesson?`
        ];

    return `<!doctype html>
<html>
<head>
    <title>${escapeWorksheetHtml(worksheet.title)}</title>
    <style>
        :root { --sc: 0.85; }
        @page { size: A4 portrait; margin: 10mm; }
        @font-face {
            font-family: 'OpenDyslexic';
            src: url('https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/woff/OpenDyslexic-Regular.woff') format('woff');
            font-display: swap;
        }
        * { box-sizing: border-box; }
        body { margin: 0; color: #2D4042; font-family: 'OpenDyslexic', Arial, Helvetica, sans-serif; background: white; }
        .page { height: 277mm; overflow: hidden; page-break-after: always; display: flex; flex-direction: column; padding: 9mm; border: 3px solid #2D4042; border-radius: 12px; background: #FFFFFF; }
        .page:last-child { page-break-after: auto; }
        .student-canvas { flex: 1 1 auto; overflow: hidden; display: flex; flex-direction: column; gap: calc(5mm * var(--sc)); }
        .worksheet-header { display: flex; justify-content: space-between; gap: 12px; font-size: calc(14pt * var(--sc)); color: #5D7375; border-bottom: 2px solid #75B9BE; padding-bottom: 3mm; font-weight: 800; flex-shrink: 0; }
        h1 { font-size: calc(26pt * var(--sc)); line-height: 1.2; margin: 2mm 0 1mm; font-weight: 800; color: #2D4042; flex-shrink: 0; }
        h2 { font-size: calc(19pt * var(--sc)); line-height: 1.25; margin: 0 0 2mm; font-weight: 800; color: #2D4042; flex-shrink: 0; }
        /* Page 1 — Visual Recognition */
        .visual-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: calc(6mm * var(--sc)); align-items: stretch; flex: 1 1 auto; min-height: 0; }
        .visual-choice, .vocab-card, .sort-item { border: 3px solid #2D4042; border-radius: 14px; box-shadow: 4px 4px 0 rgba(117, 185, 190, 0.28); }
        .visual-choice { padding: 4mm; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3mm; position: relative; text-align: center; background: #FDF6EC; }
        .visual-symbol { font-size: 72pt; line-height: 1; flex: 1 1 auto; display: flex; align-items: center; }
        .worksheet-image { width: 100%; flex: 1 1 auto; min-height: 0; object-fit: contain; border: 2px solid #75B9BE; border-radius: 10px; background: #FFFFFF; padding: 2mm; }
        .tick-box { font-size: calc(30pt * var(--sc)); line-height: 1; color: #75B9BE; flex-shrink: 0; }
        .choice-number { position: absolute; top: 3mm; left: 4mm; font-size: calc(13pt * var(--sc)); font-weight: 800; color: #5D7375; }
        /* Page 2 — Vocabulary */
        .vocab-grid { display: flex; flex-direction: column; gap: calc(4mm * var(--sc)); flex: 1 1 auto; min-height: 0; }
        .vocab-card { padding: calc(4mm * var(--sc)) calc(5mm * var(--sc)); display: flex; flex-direction: row; align-items: center; justify-content: space-between; gap: calc(6mm * var(--sc)); background: #FFFFFF; min-height: 0; flex: 1 1 auto; }
        .vocab-word { font-size: calc(20pt * var(--sc)); color: #2D4042; font-weight: 800; flex-shrink: 0; }
        .vocab-score { display: flex; gap: calc(4mm * var(--sc)); flex-shrink: 0; }
        .vocab-score-item { font-size: calc(11pt * var(--sc)); font-weight: 800; color: #2D4042; white-space: nowrap; }
        /* Page 3 — Sorting */
        .sort-columns { display: grid; grid-template-columns: 1fr 1fr; gap: calc(8mm * var(--sc)); flex: 1 1 auto; min-height: 0; }
        .sort-column { border: 3px dashed #75B9BE; border-radius: 14px; padding: calc(5mm * var(--sc)); background: #F8FAFC; min-height: 0; }
        .sort-column-title { font-size: calc(20pt * var(--sc)); font-weight: 800; text-align: center; margin-bottom: calc(5mm * var(--sc)); color: #2D4042; }
        .sort-bank { display: grid; grid-template-columns: repeat(4, 1fr); gap: calc(4mm * var(--sc)); flex-shrink: 0; }
        .sort-item { padding: calc(3mm * var(--sc)); font-size: calc(15pt * var(--sc)); font-weight: 800; background: #FFFFFF; display: flex; align-items: center; justify-content: center; text-align: center; border: 3px solid #2D4042; border-radius: 14px; box-shadow: 4px 4px 0 rgba(117,185,190,0.28); }
        /* Page 4 — Knowledge */
        .yes-no-maybe { display: flex; gap: calc(9mm * var(--sc)); font-size: calc(22pt * var(--sc)); font-weight: 800; flex-shrink: 0; flex-wrap: wrap; }
        .question-block { border: 3px solid #E2E8F0; border-radius: 14px; padding: calc(6mm * var(--sc)); background: #F8FAFC; flex: 1 1 auto; display: flex; flex-direction: column; justify-content: center; gap: calc(3mm * var(--sc)); min-height: 0; }
        /* Page 5 — Teacher Summary */
        .summary-section { flex-shrink: 0; }
        .custody-section { border: 2px solid #92400E; border-radius: 10px; background: #FFF7ED; padding: calc(3mm * var(--sc)) calc(4mm * var(--sc)); margin-bottom: calc(3mm * var(--sc)); min-height: calc(20mm * var(--sc)); display: flex; align-items: center; }
        .custody-row { display: grid; grid-template-columns: 1fr 2fr; gap: calc(6mm * var(--sc)); width: 100%; font-size: calc(12pt * var(--sc)); font-weight: 800; color: #2D4042; align-items: end; }
        .custody-field { display: grid; grid-template-columns: auto 1fr; align-items: end; gap: calc(2mm * var(--sc)); }
        .custody-label { line-height: 1.1; white-space: nowrap; }
        .custody-line { display: inline-block; border-bottom: 2px solid #2D4042; width: 100%; min-width: 0; height: 1.1em; vertical-align: bottom; }
        .summary-title { font-size: calc(11pt * var(--sc)); font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #92400E; border-bottom: 2px solid #D97706; padding-bottom: 2mm; margin-bottom: calc(3mm * var(--sc)); }
        .score-row { display: flex; justify-content: space-between; font-size: calc(12pt * var(--sc)); font-weight: 800; padding: 1.5mm 0; border-bottom: 1px dashed #D1D5DB; }
        .score-total { display: flex; justify-content: space-between; font-size: calc(15pt * var(--sc)); font-weight: 800; padding-top: calc(3mm * var(--sc)); color: #2D4042; }
        .matrix-legend { font-size: calc(10pt * var(--sc)); color: #92400E; margin-bottom: calc(3mm * var(--sc)); line-height: 1.5; }
        .matrix { display: flex; flex-direction: column; gap: calc(2mm * var(--sc)); }
        .matrix-row { display: flex; align-items: center; gap: calc(4mm * var(--sc)); padding: calc(2mm * var(--sc)) 0; border-bottom: 1px dashed #D1D5DB; }
        .matrix-label { font-size: calc(12pt * var(--sc)); font-weight: 800; min-width: calc(48mm * var(--sc)); flex-shrink: 0; }
        .matrix-checks { font-size: calc(12pt * var(--sc)); font-weight: 800; display: flex; gap: calc(4mm * var(--sc)); flex-wrap: wrap; }
        .nnow-grid { display: grid; grid-template-columns: 1fr 1fr; gap: calc(3mm * var(--sc)); margin-top: calc(3mm * var(--sc)); }
        .nnow-item { font-size: calc(12pt * var(--sc)); font-weight: 800; }
        #toolbar { display: flex; align-items: center; gap: 10px; background: #2D4042; color: white; padding: 10px 18px; font-family: Arial, sans-serif; font-size: 14px; position: sticky; top: 0; z-index: 100; }
        #toolbar button { background: #75B9BE; color: #2D4042; border: none; border-radius: 6px; font-weight: 800; cursor: pointer; font-size: 14px; padding: 5px 12px; }
        #toolbar .print-btn { background: #2D9B6F; color: white; padding: 6px 18px; font-size: 15px; margin-left: auto; }
        #toolbar .hint { font-size: 12px; color: rgba(255,255,255,0.55); }
        @media print {
            #toolbar { display: none !important; }
            body { background: white; padding-top: 0; }
            .page { break-after: page; margin: 0; }
            .page:last-child { break-after: auto; }
        }
        @media screen {
            body { background: #E5E7EB; padding-top: 0; }
            .page { width: 210mm; margin: 10mm auto; box-shadow: 0 10px 30px rgba(15,23,42,0.18); }
        }
    </style>
</head>
<body>
    <div id="toolbar">
        <span style="font-weight:800">Text size:</span>
        <button onclick="adj(-0.05)">−</button>
        <span id="sc-label" style="min-width:38px;text-align:center;font-weight:800">85%</span>
        <button onclick="adj(0.05)">+</button>
        <span class="hint">Reduce if content is cut off at the bottom of a page</span>
        <button class="print-btn" onclick="window.print()">Print</button>
    </div>

${(students.length > 0 ? students : [{ name: '' }]).map(student => {
        const n = escapeWorksheetHtml(student.name || '');
        const nameSpan = n ? `<span style="color:#2D4042;">${n}</span>` : '';
        return `
    <section class="page">
        <div class="student-canvas">
            <div class="worksheet-header">${nameSpan}<span>${escapeWorksheetHtml(worksheet.testType)}: ${escapeWorksheetHtml(worksheet.topic)}</span></div>
            <h1>${visualQuestion}</h1>
            <div class="visual-grid">
                ${renderVisualChoice(visualChoices[0], '🧰', 1)}
                ${renderVisualChoice(visualChoices[1], '🖼️', 2)}
                ${renderVisualChoice(visualChoices[2], '🚗', 3)}
            </div>
        </div>
    </section>

    <section class="page">
        <div class="student-canvas">
            <div class="worksheet-header">${nameSpan}<span>${escapeWorksheetHtml(worksheet.testType)}: ${escapeWorksheetHtml(worksheet.topic)}</span></div>
            <h1>Key words</h1>
            <div class="vocab-grid">
                ${vocabItems.slice(0, 4).map(item => `
                <div class="vocab-card">
                    <div class="vocab-word">${escapeWorksheetHtml(item?.word || 'Key Word')}</div>
                    <div class="vocab-score">
                        <span class="vocab-score-item">⬜ 0 (No Response)</span>
                        <span class="vocab-score-item">⬜ 1 (Receptive / Points)</span>
                        <span class="vocab-score-item">⬜ 2 (Expressive / Says)</span>
                    </div>
                </div>`).join('')}
            </div>
        </div>
    </section>

    <section class="page">
        <div class="student-canvas">
            <div class="worksheet-header">${nameSpan}<span>${escapeWorksheetHtml(worksheet.testType)}: ${escapeWorksheetHtml(worksheet.topic)}</span></div>
            <h1>${sortingQuestion}</h1>
            <div class="sort-columns">
                <div class="sort-column"><div class="sort-column-title">${escapeWorksheetHtml(worksheet.sorting.leftLabel)}</div></div>
                <div class="sort-column"><div class="sort-column-title">${escapeWorksheetHtml(worksheet.sorting.rightLabel)}</div></div>
            </div>
            <div class="sort-bank">
                ${sortingItems.slice(0, 4).map(item => `<div class="sort-item">${escapeWorksheetHtml(item)}</div>`).join('')}
            </div>
        </div>
    </section>

    <section class="page">
        <div class="student-canvas">
            <div class="worksheet-header">${nameSpan}<span>${escapeWorksheetHtml(worksheet.testType)}: ${escapeWorksheetHtml(worksheet.topic)}</span></div>
            <h1>Show what you know.</h1>
            ${knowledgeQuestions.slice(0, 4).map(q => `
            <div class="question-block">
                <h2>${escapeWorksheetHtml(q)}</h2>
                <div class="yes-no-maybe"><span>☐ Yes</span><span>☐ Maybe</span><span>☐ No</span></div>
            </div>`).join('')}
        </div>
    </section>

    <section class="page" style="background: #FFFBEB; border-color: #D97706;">
        <div class="student-canvas">
            <div class="worksheet-header" style="border-bottom-color: #D97706; color: #92400E;">
                ${nameSpan}<span>${escapeWorksheetHtml(worksheet.testType)}: ${escapeWorksheetHtml(worksheet.topic)}</span>
                <span style="background: #FEF3C7; padding: 2px 10px; border-radius: 6px; font-size: calc(11pt * var(--sc));">⚠ Teacher Use Only</span>
            </div>
            <div class="summary-section custody-section">
                <div class="custody-row">
                    <span class="custody-field"><span class="custody-label">Date:</span><span class="custody-line"></span></span>
                    <span class="custody-field"><span class="custody-label">Staff Name:</span><span class="custody-line"></span></span>
                </div>
            </div>
            <div class="summary-section">
                <div class="summary-title">Academic Attainment</div>
                <div class="score-row"><span>Page 1 — Visual Recognition</span><span>___ / 1</span></div>
                <div class="score-row"><span>Page 2 — Vocabulary</span><span>___ / 8 &nbsp;<span style="font-weight: 700; color: #5D7375; font-size: 0.85em;">(2 pts per word)</span></span></div>
                <div class="score-row"><span>Page 3 — Sorting</span><span>___ / 4</span></div>
                <div class="score-row"><span>Page 4 — Knowledge (Yes/No = 1, Maybe = 0.5)</span><span>___ / 4</span></div>
                <div class="score-total"><span>Total Score</span><span>___ / 17</span></div>
            </div>
            <div class="summary-section">
                <div class="summary-title">2. Communication &amp; Engagement (EHCP Tracking)</div>
                <div class="matrix-legend">Rate the highest level of independence demonstrated for each skill during this task:<br>(1 = Full Physical, 2 = Gestural, 3 = Verbal, 4 = Visual, 5 = Independent, DNE = Did Not Engage)</div>
                <div class="matrix">
                    <div class="matrix-row"><span class="matrix-label">Attention to Task</span><span class="matrix-checks"><span>⬜ 1</span><span>⬜ 2</span><span>⬜ 3</span><span>⬜ 4</span><span>⬜ 5</span><span>⬜ DNE</span></span></div>
                    <div class="matrix-row"><span class="matrix-label">Response / Output</span><span class="matrix-checks"><span>⬜ 1</span><span>⬜ 2</span><span>⬜ 3</span><span>⬜ 4</span><span>⬜ 5</span><span>⬜ DNE</span></span></div>
                    <div class="matrix-row"><span class="matrix-label">Task Independence</span><span class="matrix-checks"><span>⬜ 1</span><span>⬜ 2</span><span>⬜ 3</span><span>⬜ 4</span><span>⬜ 5</span><span>⬜ DNE</span></span></div>
                </div>
            </div>
            <div class="summary-section">
                <div class="summary-title">3. Stamina &amp; Contextual Data</div>
                <div class="score-row"><span>Task Duration (Stamina)</span><span>_____ Minutes</span></div>
                <div style="padding: calc(3mm * var(--sc)) 0; font-size: calc(12pt * var(--sc)); font-weight: 800;">
                    Regulation State:&nbsp;&nbsp;
                    <span style="margin-right: calc(6mm * var(--sc));">⬜ Green (Calm / Ready)</span>
                    <span style="margin-right: calc(6mm * var(--sc));">⬜ Amber (Distracted / Anxious)</span>
                    <span>⬜ Red (Dysregulated / In Crisis)</span>
                </div>
            </div>
        </div>
    </section>`;
    }).join('\n')}

    <script>
        var sc = 0.85;
        function adj(d) {
            sc = Math.min(1.0, Math.max(0.6, +(sc + d).toFixed(2)));
            document.documentElement.style.setProperty('--sc', sc);
            document.getElementById('sc-label').textContent = Math.round(sc * 100) + '%';
        }
    </script>
</body>
</html>`;
};

const CreationModal = ({
    isOpen,
    onClose,
    onSubmit,
    lessonNumber,
    students,
    isSupplyTeacherMode = false,
    profileGroups = [],
}) => {
    const [duration, setDuration] = useState(15);
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [selectedProfileKeys, setSelectedProfileKeys] = useState([]);

    const fixedSourceTag = `Lesson ${lessonNumber}`;

    useEffect(() => {
        if (isOpen) {
            setSelectedStudentIds(students.map(student => student._id));
            setSelectedProfileKeys(profileGroups.map(g => g.key));
            setDuration(15);
        }
    }, [isOpen, lessonNumber, students, profileGroups]);

    if (!isOpen) return null;

    const handleStudentCheckboxChange = (studentId) => {
        setSelectedStudentIds(prev => prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]);
    };

    const handleProfileCheckboxChange = (profileKey) => {
        setSelectedProfileKeys(prev => prev.includes(profileKey) ? prev.filter(k => k !== profileKey) : [...prev, profileKey]);
    };

    const handleSubmit = () => {
        if (isSupplyTeacherMode) {
            if (selectedProfileKeys.length === 0) {
                alert('Please assign at least one profile to this tutorial.');
                return;
            }
            const studentIds = profileGroups
                .filter(g => selectedProfileKeys.includes(g.key))
                .map(g => g.representative._id);
            onSubmit({ lessonNumber, durationMinutes: duration, sourceLessonTags: [fixedSourceTag], studentIds });
        } else {
            if (selectedStudentIds.length === 0) {
                alert('Please assign at least one student to this tutorial.');
                return;
            }
            onSubmit({ lessonNumber, durationMinutes: duration, sourceLessonTags: [fixedSourceTag], studentIds: selectedStudentIds });
        }
    };

    const isSubmitDisabled = isSupplyTeacherMode ? selectedProfileKeys.length === 0 : selectedStudentIds.length === 0;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45, 64, 66, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="dash-card" style={{ padding: '30px', width: '650px', maxWidth: '95%', background: 'white', maxHeight:'90vh', overflowY:'auto' }}>
                <h3 style={{marginTop: 0, color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif'}}>
                    Create Tutorial: <span style={{color: BRAND.teal}}>Lesson {lessonNumber}</span>
                </h3>

                <div style={{marginBottom: '20px'}}>
                    <label style={{display:'block', marginBottom:'5px', fontWeight:'700', fontSize:'0.9em', color: BRAND.charcoal}}>Duration (minutes)</label>
                    <input className="dash-input" type="number" value={duration} onChange={e => setDuration(e.target.value)} min="1" />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{display:'block', marginBottom:'5px', fontWeight:'700', fontSize:'0.9em', color: BRAND.charcoal}}>Lesson Content</label>
                    <div style={{ border: '2px solid #eee', padding: '12px 10px', borderRadius: '10px', background: '#F9FAFB' }}>
                        <p style={{ margin: 0, fontSize: '0.9em', fontWeight: '700', color: BRAND.teal }}>{fixedSourceTag}</p>
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{display:'block', marginBottom:'5px', fontWeight:'700', fontSize:'0.9em', color: BRAND.charcoal}}>
                        Assign {isSupplyTeacherMode ? 'Profiles' : 'Students'}
                    </label>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '2px solid #eee', padding: '10px', borderRadius: '10px', background: '#F9FAFB' }}>
                        {isSupplyTeacherMode ? (
                            profileGroups.length > 0 ? (
                                profileGroups.map(group => (
                                    <div key={group.key} style={{marginBottom:'8px'}}>
                                        <label style={{cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', fontSize:'0.9em', fontWeight: '500'}}>
                                            <input
                                                type="checkbox"
                                                checked={selectedProfileKeys.includes(group.key)}
                                                onChange={() => handleProfileCheckboxChange(group.key)}
                                            />
                                            <span style={{fontWeight:'700', color: BRAND.charcoal}}>{group.label}</span>
                                            <span style={{fontSize:'0.8em', background: BRAND.teal, color:'white', borderRadius:'999px', padding:'1px 8px', fontWeight:'700'}}>×{group.students.length}</span>
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p style={{ fontStyle: 'italic', color: '#888', fontSize: '0.85em', margin:0 }}>No profiles created yet.</p>
                            )
                        ) : (
                            students && students.length > 0 ? (
                                students.map(student => (
                                    <div key={student._id} style={{marginBottom:'5px'}}>
                                        <label style={{cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', fontSize:'0.9em', fontWeight: '500'}}>
                                            <input
                                                type="checkbox"
                                                checked={selectedStudentIds.includes(student._id)}
                                                onChange={() => handleStudentCheckboxChange(student._id)}
                                            />
                                            {student.name}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p style={{ fontStyle: 'italic', color: '#888', fontSize: '0.85em', margin:0 }}>No students in this schedule.</p>
                            )
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap:'10px', marginTop:'25px' }}>
                    <button onClick={onClose} className="btn-base" style={{background:'#F0F2F5', color: BRAND.charcoal}}>Cancel</button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        className="btn-base btn-teal"
                        style={{ opacity: isSubmitDisabled ? 0.6 : 1 }}
                    >
                        Generate Tutorial
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- FONT DROPDOWN (renders each option in its own font) ---
const FONT_OPTIONS = [
    { value: 'OpenDyslexic', fontFamily: "'OpenDyslexic', sans-serif" },
    { value: 'Nunito',       fontFamily: "'Nunito', sans-serif" },
    { value: 'Arial',        fontFamily: 'Arial, sans-serif' },
];

const FontDropdown = ({ value, onChange }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const selected = FONT_OPTIONS.find(opt => opt.value === value) || FONT_OPTIONS[0];

    return (
        <div ref={ref} style={{ position: 'relative', userSelect: 'none' }}>
            <style>{`
                @font-face {
                    font-family: 'OpenDyslexic';
                    src: url('https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/woff/OpenDyslexic-Regular.woff') format('woff');
                    font-display: swap;
                }
            `}</style>
            <button
                type="button"
                onClick={() => setOpen(prev => !prev)}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    border: `2px solid ${BRAND.charcoal}`,
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    fontFamily: selected.fontFamily,
                    fontSize: '0.95em',
                    color: BRAND.charcoal,
                    fontWeight: '600',
                }}
            >
                {selected.value}
                <span style={{ fontSize: '0.75em', marginLeft: '8px', opacity: 0.6 }}>▼</span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: `2px solid ${BRAND.charcoal}`,
                    borderRadius: '8px',
                    zIndex: 999,
                    overflow: 'hidden',
                    boxShadow: '3px 3px 0px rgba(45,64,66,0.12)',
                }}>
                    {FONT_OPTIONS.map((opt, i) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            style={{
                                width: '100%',
                                display: 'block',
                                padding: '10px 12px',
                                border: 'none',
                                borderTop: i > 0 ? '1px solid #E5E7EB' : 'none',
                                background: opt.value === value ? '#EFF9FA' : 'white',
                                cursor: 'pointer',
                                fontFamily: opt.fontFamily,
                                fontSize: '0.95em',
                                color: BRAND.charcoal,
                                fontWeight: opt.value === value ? '700' : '500',
                                textAlign: 'left',
                            }}
                        >
                            {opt.value}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- SUPPLY TEACHER: PROFILE HELPERS ---
const getProfileKey = (student) => {
    if (student.noCustomScaffoldingRequired === true) return '__mainstream__';
    const parts = [];
    if (student.readingAgeLabel) parts.push(`ra:${student.readingAgeLabel}`);
    const senConds = (Array.isArray(student.senConditions) && student.senConditions.length > 0)
        ? student.senConditions.filter(s => s && s !== 'None')
        : (student.senCondition && student.senCondition !== 'None' ? [student.senCondition] : []);
    if (senConds.length > 0) parts.push(`sen:${[...senConds].sort().join(',')}`);
    if (student.eal && student.primaryLanguage) parts.push(`eal:${student.primaryLanguage}`);
    return parts.length > 0 ? parts.join('|') : '__mainstream__';
};

const getProfileLabel = (student) => {
    if (student.noCustomScaffoldingRequired === true) return 'Mainstream';
    const parts = [];
    if (student.readingAgeLabel) parts.push(`${student.readingAgeLabel} Reading Age`);
    const senConds = (Array.isArray(student.senConditions) && student.senConditions.length > 0)
        ? student.senConditions.filter(s => s && s !== 'None')
        : (student.senCondition && student.senCondition !== 'None' ? [student.senCondition] : []);
    senConds.forEach(c => parts.push(c));
    if (student.eal && student.primaryLanguage) parts.push(student.primaryLanguage);
    return parts.length > 0 ? parts.join(' + ') : 'Mainstream';
};

// --- MODAL: ADD/EDIT PROFILE (SUPPLY TEACHER) ---
const ProfileCreationModal = ({ isOpen, onClose, onSubmit, yearGroup, primaryLanguageOptions = [], languagesLoading = false, mode = 'create', initialProfile = null }) => {
    const [count, setCount] = useState(1);
    const [showScaffolding, setShowScaffolding] = useState(false);
    const [isDifferentReadingAge, setIsDifferentReadingAge] = useState(false);
    const [readingAgeOption, setReadingAgeOption] = useState('slightly_lower');
    const [selectedSenConditions, setSelectedSenConditions] = useState([]);
    const [eal, setEal] = useState(false);
    const [primaryLanguage, setPrimaryLanguage] = useState('');
    const [glossaryLanguageMode, setGlossaryLanguageMode] = useState('PRIMARY_TEXT_ENGLISH_GLOSSARY');
    const [audioNarrationEnabled, setAudioNarrationEnabled] = useState(false);
    const [audioNarrationSpeed, setAudioNarrationSpeed] = useState(1);
    const [tutorialFont, setTutorialFont] = useState('OpenDyslexic');

    const isEditMode = mode === 'edit';

    const inferAgeFromYearGroup = (group) => {
        if (!group) return 11;
        const rangeMatch = String(group).match(/(\d{1,2})\s*[-–]\s*(\d{1,2})/);
        if (rangeMatch) return parseInt(rangeMatch[2], 10);
        const yearMatch = String(group).match(/year\s*(\d{1,2})/i);
        if (yearMatch) return parseInt(yearMatch[1], 10) + 5;
        const anyNumber = String(group).match(/(\d{1,2})/);
        if (anyNumber) return parseInt(anyNumber[1], 10);
        return 11;
    };

    const senOptions = ['Dyslexia', 'Working Memory Difficulties', 'Processing Difficulties', 'Language/Communication Needs', 'Autism Spectrum Condition'];

    const toggleSenCondition = (condition) => {
        setSelectedSenConditions(prev => prev.includes(condition) ? prev.filter(c => c !== condition) : [...prev, condition]);
    };

    useEffect(() => {
        if (!isOpen) return;
        if (isEditMode && initialProfile) {
            const rep = initialProfile.representative;
            setCount(initialProfile.students.length);
            setIsDifferentReadingAge(!!rep.readingAgeLabel);
            setReadingAgeOption(rep.readingAgeLabel === 'Much Lower' ? 'much_lower' : 'slightly_lower');
            const senConds = (Array.isArray(rep.senConditions) && rep.senConditions.length > 0)
                ? rep.senConditions.filter(s => s && s !== 'None')
                : (rep.senCondition && rep.senCondition !== 'None' ? [rep.senCondition] : []);
            setSelectedSenConditions(senConds);
            setEal(rep.eal === true);
            setShowScaffolding(rep.noCustomScaffoldingRequired !== true);
            setPrimaryLanguage(rep.primaryLanguage || '');
            setGlossaryLanguageMode(rep.glossaryLanguageMode || 'PRIMARY_TEXT_ENGLISH_GLOSSARY');
            setAudioNarrationEnabled(rep.audioNarrationEnabled === true);
            setAudioNarrationSpeed([0.5, 0.75, 1].includes(Number(rep.audioNarrationSpeed)) ? Number(rep.audioNarrationSpeed) : 1);
            setTutorialFont(['OpenDyslexic', 'Nunito', 'Arial'].includes(rep.tutorialFont) ? rep.tutorialFont : 'OpenDyslexic');
        } else if (!isEditMode) {
            setCount(1);
            setShowScaffolding(false);
            setIsDifferentReadingAge(false);
            setReadingAgeOption('slightly_lower');
            setSelectedSenConditions([]);
            setEal(false);
            setPrimaryLanguage('');
            setGlossaryLanguageMode('PRIMARY_TEXT_ENGLISH_GLOSSARY');
            setAudioNarrationEnabled(false);
            setAudioNarrationSpeed(1);
            setTutorialFont('OpenDyslexic');
        }
    }, [isOpen, isEditMode, initialProfile]);

    if (!isOpen) return null;

    // Dynamic profile label — mirrors getProfileLabel logic
    const previewReadingAgeLabel = isDifferentReadingAge ? (readingAgeOption === 'much_lower' ? 'Much Lower' : 'Slightly Lower') : '';
    const previewParts = [];
    if (showScaffolding) {
        if (previewReadingAgeLabel) previewParts.push(`${previewReadingAgeLabel} Reading Age`);
        selectedSenConditions.forEach(c => previewParts.push(c));
        if (eal && primaryLanguage) previewParts.push(primaryLanguage);
    }
    const previewLabel = previewParts.length > 0 ? previewParts.join(' + ') : 'Mainstream';
    const isMainstream = previewLabel === 'Mainstream';

    const handleSubmit = () => {
        const inferredAge = inferAgeFromYearGroup(yearGroup);
        const readingAgeValue = isDifferentReadingAge
            ? (readingAgeOption === 'much_lower' ? Math.max(5, inferredAge - 4) : Math.max(5, inferredAge - 2))
            : inferredAge;
        const noCustom = !isDifferentReadingAge && selectedSenConditions.length === 0 && !eal;
        onSubmit({
            age: inferredAge,
            readingAge: String(readingAgeValue),
            readingAgeLabel: isDifferentReadingAge ? (readingAgeOption === 'much_lower' ? 'Much Lower' : 'Slightly Lower') : '',
            senCondition: selectedSenConditions[0] || 'None',
            senConditions: selectedSenConditions,
            eal,
            primaryLanguage,
            glossaryLanguageMode,
            customTutorialStructure: false,
            keyTermsPerMatchingPage: 4,
            audioNarrationEnabled,
            audioNarrationSpeed,
            noCustomScaffoldingRequired: noCustom,
            tutorialFont,
        }, count);
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45,64,66,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '16px' }}>
            <div onClick={e => e.stopPropagation()} className="dash-card" style={{ padding: '30px', width: showScaffolding ? '450px' : '420px', maxWidth: '95%', background: 'white', maxHeight: '90vh', overflowY: 'auto', color: BRAND.charcoal }}>
                <h3 style={{ marginTop: 0, fontFamily: 'Nunito, sans-serif' }}>
                    {isEditMode ? 'Edit Profile' : 'Add Profile'}
                </h3>

                {/* Dynamic profile label */}
                <div style={{ marginBottom: '20px', padding: '10px 14px', borderRadius: '10px', border: `2px solid ${isMainstream ? '#B0C4C6' : BRAND.teal}`, background: isMainstream ? '#F0F2F5' : '#EAF6F7', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.75em', fontWeight: '800', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Profile:</span>
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '1.15em', color: isMainstream ? BRAND.charcoal : BRAND.teal }}>{previewLabel}</span>
                </div>

                {/* Count */}
                <div style={{ marginBottom: '15px' }}>
                    <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Number of Students</label>
                    <input
                        className="dash-input"
                        type="number"
                        min="1"
                        max="60"
                        value={count}
                        onChange={e => setCount(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                </div>

                {/* Scaffolding toggle */}
                <div style={{ marginBottom: showScaffolding ? '20px' : '25px', display:'flex', alignItems:'center', gap:'12px', background:'#F8F9FA', padding:'10px 12px', borderRadius: '10px', border: '2px solid #E0E0E0' }}>
                    <input
                        type="checkbox"
                        id="profileScaffoldingToggle"
                        checked={showScaffolding}
                        onChange={e => setShowScaffolding(e.target.checked)}
                        style={{width:'18px', height:'18px', cursor:'pointer'}}
                    />
                    <label htmlFor="profileScaffoldingToggle" style={{cursor:'pointer', fontWeight:'700', color: BRAND.charcoal, fontSize:'0.95em'}}>Add Custom Scaffolding</label>
                </div>

                {showScaffolding && (
                    <>
                        {/* Reading Age */}
                        <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px', background: '#F8F9FA', padding: '10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                            <input
                                type="checkbox"
                                id="profileDifferentReadingAgeToggle"
                                checked={isDifferentReadingAge}
                                onChange={e => setIsDifferentReadingAge(e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <label htmlFor="profileDifferentReadingAgeToggle" style={{ cursor: 'pointer', fontWeight: '700', color: BRAND.charcoal, fontSize: '0.95em' }}>Different Reading Age?</label>
                        </div>
                        {isDifferentReadingAge && (
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', fontWeight: '700', color: BRAND.charcoal }}>Reading Age</label>
                                <select className="dash-input" value={readingAgeOption} onChange={e => setReadingAgeOption(e.target.value)}>
                                    <option value="slightly_lower">Slightly Lower</option>
                                    <option value="much_lower">Much Lower</option>
                                </select>
                            </div>
                        )}

                        {/* SEN Conditions */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{display:'block', marginBottom:'8px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>SEN Conditions</label>
                            <div style={{ border: '2px solid #eee', borderRadius: '10px', padding: '10px', background: '#F9FAFB' }}>
                                {senOptions.map(option => (
                                    <label key={option} style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', fontSize:'0.9em', fontWeight:'500', color: BRAND.charcoal, cursor:'pointer'}}>
                                        <input
                                            type="checkbox"
                                            checked={selectedSenConditions.includes(option)}
                                            onChange={() => toggleSenCondition(option)}
                                        />
                                        {option}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* EAL */}
                        <div style={{ marginBottom: '15px', display:'flex', alignItems:'center', gap:'12px', background:'#F8F9FA', padding:'10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                            <input
                                type="checkbox"
                                id="profileEalToggle"
                                checked={eal}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    setEal(checked);
                                    if (!checked) {
                                        setPrimaryLanguage('');
                                    }
                                }}
                                style={{width:'18px', height:'18px', cursor:'pointer'}}
                            />
                            <label htmlFor="profileEalToggle" style={{cursor:'pointer', fontWeight:'700', color: BRAND.charcoal, fontSize:'0.95em'}}>EAL Student?</label>
                        </div>

                        {eal && (
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Primary Language</label>
                                <select className="dash-input" value={primaryLanguage} onChange={e => setPrimaryLanguage(e.target.value)} disabled={languagesLoading}>
                                    <option value="">{languagesLoading ? 'Loading languages...' : 'Select primary language'}</option>
                                    {primaryLanguageOptions.map(lang => {
                                        const val = lang.name || lang;
                                        return <option key={lang.code || val} value={val}>{val}</option>;
                                    })}
                                </select>
                            </div>
                        )}

                        {eal && (
                            <div style={{ marginBottom: '25px' }}>
                                <label style={{display:'block', marginBottom:'6px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Translation Direction</label>
                                <label style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', fontSize:'0.9em', color: BRAND.charcoal }}>
                                    <input type="radio" name="profileGlossaryMode" value="PRIMARY_TEXT_ENGLISH_GLOSSARY" checked={glossaryLanguageMode === 'PRIMARY_TEXT_ENGLISH_GLOSSARY'} onChange={e => setGlossaryLanguageMode(e.target.value)} />
                                    Primary Language Text + English Glossary
                                </label>
                                <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'0.9em', color: BRAND.charcoal }}>
                                    <input type="radio" name="profileGlossaryMode" value="ENGLISH_TEXT_PRIMARY_GLOSSARY" checked={glossaryLanguageMode === 'ENGLISH_TEXT_PRIMARY_GLOSSARY'} onChange={e => setGlossaryLanguageMode(e.target.value)} />
                                    English Text + Primary Language Glossary
                                </label>
                            </div>
                        )}

                        {/* Audio Narration */}
                        <div style={{ marginBottom: '15px', display:'flex', alignItems:'center', gap:'12px', background:'#F8F9FA', padding:'10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                            <input
                                type="checkbox"
                                id="profileAudioToggle"
                                checked={audioNarrationEnabled}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    setAudioNarrationEnabled(checked);
                                    if (!checked) setAudioNarrationSpeed(1);
                                }}
                                style={{width:'18px', height:'18px', cursor:'pointer'}}
                            />
                            <label htmlFor="profileAudioToggle" style={{cursor:'pointer', fontWeight:'700', color: BRAND.charcoal, fontSize:'0.95em'}}>Enable Audio Narration?</label>
                        </div>
                        {audioNarrationEnabled && (
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Default Audio Playback Speed</label>
                                <select className="dash-input" value={audioNarrationSpeed} onChange={e => setAudioNarrationSpeed(Number(e.target.value))}>
                                    <option value={0.5}>0.5x</option>
                                    <option value={0.75}>0.75x</option>
                                    <option value={1}>1x</option>
                                </select>
                            </div>
                        )}
                    </>
                )}

                {/* Tutorial Font */}
                <div style={{ marginBottom: '20px', background:'#F8F9FA', padding:'10px 12px', borderRadius: '10px', border: '2px solid #E0E0E0' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9em', fontWeight: '700', color: BRAND.charcoal }}>
                        Tutorial Font
                    </label>
                    <FontDropdown value={tutorialFont} onChange={setTutorialFont} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button type="button" onClick={onClose} className="btn-base" style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Cancel</button>
                    <button type="button" onClick={handleSubmit} className="btn-base btn-teal">
                        {isEditMode ? 'Save Profile' : `Add ${count} Student${count !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MODAL: ADD STUDENT ---
const StudentCreationModal = ({ isOpen, onClose, onSubmit, yearGroup, primaryLanguageOptions = [], languagesLoading = false, mode = 'create', initialData = null, isSupplyTeacherMode = false, readingAgeMode = 'default', currentStudentCount = 0 }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [customScaffoldingRequired, setCustomScaffoldingRequired] = useState(false);
    const [age, setAge] = useState('');
    const [readingAge, setReadingAge] = useState('');
    const [selectedSenConditions, setSelectedSenConditions] = useState([]);
    const [eal, setEal] = useState(false);
    const [primaryLanguage, setPrimaryLanguage] = useState('');
    const [glossaryLanguageMode, setGlossaryLanguageMode] = useState('PRIMARY_TEXT_ENGLISH_GLOSSARY');
    const [customTutorialStructure, setCustomTutorialStructure] = useState(false);
    const [keyTermsPerMatchingPage, setKeyTermsPerMatchingPage] = useState('auto');
    const [audioNarrationEnabled, setAudioNarrationEnabled] = useState(false);
    const [audioNarrationSpeed, setAudioNarrationSpeed] = useState(1);
    const [tutorialFont, setTutorialFont] = useState('OpenDyslexic');
    const [readingAgeOption, setReadingAgeOption] = useState('slightly_lower');
    const [isDifferentReadingAge, setIsDifferentReadingAge] = useState(false);

    const isEditMode = mode === 'edit';
    const modalTitle = isEditMode ? 'Edit Student Scaffolding' : 'Add New Student';

    const senOptions = [
        'None',
        'Dyslexia',
        'Working Memory Difficulties',
        'Processing Difficulties',
        'Language/Communication Needs',
        'Autism Spectrum Condition',
    ];

    const inferAgeFromYearGroup = (group) => {
        if (!group) return 11;

        const rangeMatch = String(group).match(/(\d{1,2})\s*[-–]\s*(\d{1,2})/);
        if (rangeMatch) {
            return parseInt(rangeMatch[2], 10);
        }

        const yearMatch = String(group).match(/year\s*(\d{1,2})/i);
        if (yearMatch) {
            return parseInt(yearMatch[1], 10) + 5;
        }

        const anyNumber = String(group).match(/(\d{1,2})/);
        if (anyNumber) {
            return parseInt(anyNumber[1], 10);
        }

        return 11;
    };

    const handleAgeChange = (value) => {
        const numbersOnly = value.replace(/[^0-9]/g, '');
        setAge(numbersOnly);
    };

    const handleReadingAgeChange = (value) => {
        const numbersOnly = value.replace(/[^0-9]/g, '');
        setReadingAge(numbersOnly);
    };

    const resetForm = () => {
        setName('');
        setEmail('');
        setCustomScaffoldingRequired(false);
        setAge('');
        setReadingAge('');
        setSelectedSenConditions([]);
        setEal(false);
        setPrimaryLanguage('');
        setGlossaryLanguageMode('PRIMARY_TEXT_ENGLISH_GLOSSARY');
        setCustomTutorialStructure(false);
        setKeyTermsPerMatchingPage('4');
        setAudioNarrationEnabled(false);
        setAudioNarrationSpeed(1);
        setTutorialFont('OpenDyslexic');
        setReadingAgeOption('slightly_lower');
        setIsDifferentReadingAge(false);
    };

    useEffect(() => {
        if (!isOpen) return;

        if (isEditMode && initialData) {
            setName(initialData.name || '');
            setEmail(initialData.email || '');
            const hasCustom = !(initialData.noCustomScaffoldingRequired === true);
            setCustomScaffoldingRequired(hasCustom);
            setAge(String(initialData.age || ''));
            setReadingAge(String(initialData.readingAge || ''));
            const initialConditions = Array.isArray(initialData.senConditions) && initialData.senConditions.length > 0
                ? initialData.senConditions.filter(Boolean)
                : (initialData.senCondition && initialData.senCondition !== 'None' ? [initialData.senCondition] : []);
            setSelectedSenConditions(initialConditions);
            setEal(initialData.eal === true);
            setPrimaryLanguage(initialData.primaryLanguage || '');
            setGlossaryLanguageMode(initialData.glossaryLanguageMode || 'PRIMARY_TEXT_ENGLISH_GLOSSARY');
            setCustomTutorialStructure(initialData.customTutorialStructure === true);
            setKeyTermsPerMatchingPage([1, 2].includes(Number(initialData.keyTermsPerMatchingPage)) ? String(initialData.keyTermsPerMatchingPage) : 'auto');
            setAudioNarrationEnabled(initialData.audioNarrationEnabled === true);
            setAudioNarrationSpeed([0.5, 0.75, 1].includes(Number(initialData.audioNarrationSpeed)) ? Number(initialData.audioNarrationSpeed) : 1);
            setTutorialFont(['OpenDyslexic', 'Nunito', 'Arial'].includes(initialData.tutorialFont) ? initialData.tutorialFont : 'OpenDyslexic');
        } else if (!isEditMode) {
            resetForm();
        }
    }, [isOpen, isEditMode, initialData]);

    if (!isOpen) return null;

    const toggleSenCondition = (condition) => {
        setSelectedSenConditions((prev) =>
            prev.includes(condition)
                ? prev.filter((item) => item !== condition)
                : [...prev, condition]
        );
    };

    const handleSubmit = () => {
        // ── Supply Teacher mode: anonymous student, relative reading age ──
        if (isSupplyTeacherMode && !isEditMode) {
            const inferredAge = inferAgeFromYearGroup(yearGroup);
            const readingAgeValue = isDifferentReadingAge
                ? (readingAgeOption === 'much_lower' ? Math.max(5, inferredAge - 4) : Math.max(5, inferredAge - 2))
                : inferredAge;
            const readingAgeLabelValue = isDifferentReadingAge
                ? (readingAgeOption === 'much_lower' ? 'Much Lower' : 'Slightly Lower')
                : '';
            onSubmit({
                name: `Student ${currentStudentCount + 1}`,
                email: '',
                age: inferredAge,
                readingAge: String(readingAgeValue),
                readingAgeLabel: readingAgeLabelValue,
                senCondition: selectedSenConditions[0] || 'None',
                senConditions: selectedSenConditions,
                eal,
                primaryLanguage,
                glossaryLanguageMode,
                customTutorialStructure: false,
                keyTermsPerMatchingPage: 4,
                audioNarrationEnabled,
                audioNarrationSpeed,
                noCustomScaffoldingRequired: !isDifferentReadingAge,
                tutorialFont,
            });
            resetForm();
            return;
        }

        if (!isEditMode && !name.trim()) {
            alert('Please fill in Name.');
            return;
        }

        const normalizedEmail = isEditMode ? email.trim().toLowerCase() : '';
        if (normalizedEmail) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(normalizedEmail)) {
                alert('Please provide a valid email address.');
                return;
            }
        }

        if (customScaffoldingRequired) {
            let finalAge;
            let finalReadingAge;
            let readingAgeLabelValue = '';

            if (readingAgeMode === 'lower' && !isSupplyTeacherMode && !isEditMode) {
                // Age derived from year group — not entered manually
                finalAge = inferAgeFromYearGroup(yearGroup);
                finalReadingAge = isDifferentReadingAge
                    ? (readingAgeOption === 'much_lower' ? Math.max(5, finalAge - 4) : Math.max(5, finalAge - 2))
                    : finalAge;
                readingAgeLabelValue = isDifferentReadingAge
                    ? (readingAgeOption === 'much_lower' ? 'Much Lower' : 'Slightly Lower')
                    : '';
            } else {
                const finalAgeInput = parseInt(age, 10);
                if (!age) { alert('Please fill in Age.'); return; }
                if (Number.isNaN(finalAgeInput) || finalAgeInput < 5 || finalAgeInput > 18) {
                    alert('Age must be a number between 5 and 18.');
                    return;
                }
                finalAge = finalAgeInput;

                finalReadingAge = parseInt(readingAge, 10);
                if (!readingAge) { alert('Please fill in Reading Age.'); return; }
                if (Number.isNaN(finalReadingAge) || finalReadingAge < 5 || finalReadingAge > 18) {
                    alert('Reading Age must be a number between 5 and 18.');
                    return;
                }
            }

            if (eal && !primaryLanguage) {
                alert('Please select a primary language for EAL students.');
                return;
            }

            onSubmit({
                ...(isEditMode ? {} : { name: name.trim() }),
                email: normalizedEmail,
                age: finalAge,
                readingAge: String(finalReadingAge),
                readingAgeLabel: readingAgeLabelValue,
                senCondition: selectedSenConditions[0] || 'None',
                senConditions: selectedSenConditions,
                eal,
                primaryLanguage: eal ? primaryLanguage : '',
                glossaryLanguageMode: eal ? glossaryLanguageMode : 'PRIMARY_TEXT_ENGLISH_GLOSSARY',
                customTutorialStructure: isEditMode ? customTutorialStructure : false,
                keyTermsPerMatchingPage: isEditMode && customTutorialStructure && keyTermsPerMatchingPage !== 'auto' ? Number(keyTermsPerMatchingPage) : null,
                audioNarrationEnabled,
                audioNarrationSpeed,
                noCustomScaffoldingRequired: false,
                tutorialFont,
            });
        } else {
            const inferredAge = inferAgeFromYearGroup(yearGroup);
            onSubmit({
                ...(isEditMode ? {} : { name: name.trim() }),
                email: normalizedEmail,
                age: inferredAge,
                readingAge: String(inferredAge),
                senCondition: 'None',
                senConditions: [],
                eal: false,
                primaryLanguage: '',
                glossaryLanguageMode: 'PRIMARY_TEXT_ENGLISH_GLOSSARY',
                customTutorialStructure: isEditMode ? customTutorialStructure : false,
                keyTermsPerMatchingPage: null,
                audioNarrationEnabled: false,
                audioNarrationSpeed: 1,
                noCustomScaffoldingRequired: true,
                tutorialFont,
            });
        }

        if (!isEditMode) {
            resetForm();
        }
    };

    const handleClose = () => {
        if (!isEditMode) {
            resetForm();
        }
        onClose();
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45, 64, 66, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
            <div className="dash-card" style={{ padding: '30px', width: customScaffoldingRequired ? '450px' : '420px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', color: BRAND.charcoal, background: 'white' }}>
                <h3 style={{marginTop: 0, fontFamily: 'Nunito, sans-serif'}}>{modalTitle}</h3>

                {!isEditMode && !isSupplyTeacherMode && (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Name</label>
                        <input className="dash-input" type="text" value={name} onChange={e => setName(e.target.value)} />
                    </div>
                )}
                {!isEditMode && isSupplyTeacherMode && (
                    <div style={{ marginBottom: '15px', padding: '10px 12px', background: '#F0FDFA', borderRadius: '10px', border: '1px solid #80DEEA' }}>
                        <p style={{ margin: 0, fontSize: '0.9em', fontWeight: '700', color: '#006064' }}>
                            Student will be added as <strong>Student {currentStudentCount + 1}</strong>
                        </p>
                    </div>
                )}

                {isEditMode && (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Student Email</label>
                        <input
                            className="dash-input"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="student@example.com"
                        />
                    </div>
                )}

                <div style={{ marginBottom: customScaffoldingRequired ? '20px' : '25px', display:'flex', alignItems:'center', gap:'12px', background:'#F8F9FA', padding:'10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                    <input
                        type="checkbox"
                        id="customScaffoldingToggle"
                        checked={customScaffoldingRequired}
                        onChange={e => setCustomScaffoldingRequired(e.target.checked)}
                        style={{width:'18px', height:'18px', cursor:'pointer'}}
                    />
                    <label htmlFor="customScaffoldingToggle" style={{cursor:'pointer', fontWeight:'700', color: BRAND.charcoal, fontSize:'0.95em'}}>Add Custom Scaffolding</label>
                </div>

                {customScaffoldingRequired && (
                    <>
                        {(isSupplyTeacherMode && !isEditMode) && (
                            <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px', background: '#F8F9FA', padding: '10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                                <input
                                    type="checkbox"
                                    id="differentReadingAgeToggle"
                                    checked={isDifferentReadingAge}
                                    onChange={e => setIsDifferentReadingAge(e.target.checked)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <label htmlFor="differentReadingAgeToggle" style={{ cursor: 'pointer', fontWeight: '700', color: BRAND.charcoal, fontSize: '0.95em' }}>Different Reading Age?</label>
                            </div>
                        )}
                        {(isSupplyTeacherMode && !isEditMode && isDifferentReadingAge) && (
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', fontWeight: '700', color: BRAND.charcoal }}>Reading Age</label>
                                <select className="dash-input" value={readingAgeOption} onChange={e => setReadingAgeOption(e.target.value)}>
                                    <option value="slightly_lower">Slightly Lower</option>
                                    <option value="much_lower">Much Lower</option>
                                </select>
                            </div>
                        )}
                        {!isSupplyTeacherMode && readingAgeMode === 'lower' && !isEditMode && (
                            <>
                                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px', background: '#F8F9FA', padding: '10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                                    <input
                                        type="checkbox"
                                        id="differentReadingAgeToggle"
                                        checked={isDifferentReadingAge}
                                        onChange={e => setIsDifferentReadingAge(e.target.checked)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <label htmlFor="differentReadingAgeToggle" style={{ cursor: 'pointer', fontWeight: '700', color: BRAND.charcoal, fontSize: '0.95em' }}>Different Reading Age?</label>
                                </div>
                                {isDifferentReadingAge && (
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', fontWeight: '700', color: BRAND.charcoal }}>Reading Age</label>
                                        <select className="dash-input" value={readingAgeOption} onChange={e => setReadingAgeOption(e.target.value)}>
                                            <option value="slightly_lower">Slightly Lower</option>
                                            <option value="much_lower">Much Lower</option>
                                        </select>
                                    </div>
                                )}
                            </>
                        )}
                        {!isSupplyTeacherMode && readingAgeMode !== 'lower' && (
                        <div style={{ display:'flex', gap:'15px', marginBottom: '15px' }}>
                            <div style={{flex:1}}>
                                <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Age</label>
                                <input className="dash-input" type="number" min="5" max="18" step="1" value={age} onChange={e => handleAgeChange(e.target.value)} />
                            </div>
                            <div style={{flex:1}}>
                                <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Reading Age</label>
                                <input
                                    className="dash-input"
                                    type="number"
                                    min="5"
                                    max="18"
                                    step="1"
                                    value={readingAge}
                                    onChange={e => handleReadingAgeChange(e.target.value)}
                                />
                            </div>
                        </div>
                        )}

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{display:'block', marginBottom:'8px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>SEN Conditions</label>
                            <div style={{ border: '2px solid #eee', borderRadius: '10px', padding: '10px', background: '#F9FAFB' }}>
                                {senOptions.filter(option => option !== 'None').map(option => (
                                    <label key={option} style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', fontSize:'0.9em', fontWeight:'500', color: BRAND.charcoal, cursor:'pointer'}}>
                                        <input
                                            type="checkbox"
                                            checked={selectedSenConditions.includes(option)}
                                            onChange={() => toggleSenCondition(option)}
                                        />
                                        {option}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div style={{ marginBottom: '15px', display:'flex', alignItems:'center', gap:'12px', background:'#F8F9FA', padding:'10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                            <input
                                type="checkbox"
                                id="audioToggle"
                                checked={audioNarrationEnabled}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    setAudioNarrationEnabled(checked);
                                    if (!checked) setAudioNarrationSpeed(1);
                                }}
                                style={{width:'18px', height:'18px', cursor:'pointer'}}
                            />
                            <label htmlFor="audioToggle" style={{cursor:'pointer', fontWeight:'700', color: BRAND.charcoal, fontSize:'0.95em'}}>Enable Audio Narration?</label>
                        </div>

                        {audioNarrationEnabled && (
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Default Audio Playback Speed</label>
                                <select className="dash-input" value={audioNarrationSpeed} onChange={e => setAudioNarrationSpeed(Number(e.target.value))}>
                                    <option value={0.5}>0.5x</option>
                                    <option value={0.75}>0.75x</option>
                                    <option value={1}>1x</option>
                                </select>
                            </div>
                        )}

                        <div style={{ marginBottom: '15px', display:'flex', alignItems:'center', gap:'12px', background:'#F8F9FA', padding:'10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                            <input
                                type="checkbox"
                                id="ealToggle"
                                checked={eal}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    setEal(checked);
                                    if (!checked) {
                                        setPrimaryLanguage('');
                                    }
                                }}
                                style={{width:'18px', height:'18px', cursor:'pointer'}}
                            />
                            <label htmlFor="ealToggle" style={{cursor:'pointer', fontWeight:'700', color: BRAND.charcoal, fontSize:'0.95em'}}>EAL Student?</label>
                        </div>

                        {eal && (
                            <div style={{ marginBottom: '25px' }}>
                                <div style={{ marginBottom: '12px' }}>
                                    <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Primary Language</label>
                                    <select className="dash-input" value={primaryLanguage} onChange={e => setPrimaryLanguage(e.target.value)} disabled={languagesLoading}>
                                        <option value="">{languagesLoading ? 'Loading languages...' : 'Select primary language'}</option>
                                        {primaryLanguageOptions.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>

                                {isEditMode && (
                                    <div>
                                        <label style={{display:'block', marginBottom:'6px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Translation Direction</label>
                                        <label style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', fontSize:'0.9em', color: BRAND.charcoal }}>
                                            <input
                                                type="radio"
                                                name="editGlossaryLanguageMode"
                                                value="PRIMARY_TEXT_ENGLISH_GLOSSARY"
                                                checked={glossaryLanguageMode === 'PRIMARY_TEXT_ENGLISH_GLOSSARY'}
                                                onChange={(e) => setGlossaryLanguageMode(e.target.value)}
                                            />
                                            Primary Language Text + English Glossary
                                        </label>
                                        <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'0.9em', color: BRAND.charcoal }}>
                                            <input
                                                type="radio"
                                                name="editGlossaryLanguageMode"
                                                value="ENGLISH_TEXT_PRIMARY_GLOSSARY"
                                                checked={glossaryLanguageMode === 'ENGLISH_TEXT_PRIMARY_GLOSSARY'}
                                                onChange={(e) => setGlossaryLanguageMode(e.target.value)}
                                            />
                                            English Text + Primary Language Glossary
                                        </label>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {isEditMode && (
                    <div style={{ marginBottom: customTutorialStructure ? '15px' : '20px', display:'flex', alignItems:'center', gap:'12px', background:'#F8F9FA', padding:'10px 12px', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'12px', width: '100%' }}>
                            <input
                                type="checkbox"
                                id="customTutorialStructureToggle"
                                checked={customTutorialStructure}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomTutorialStructure(checked);
                                    if (!checked) setKeyTermsPerMatchingPage('4');
                                }}
                                style={{width:'18px', height:'18px', cursor:'pointer'}}
                            />
                            <label htmlFor="customTutorialStructureToggle" style={{cursor:'pointer', fontWeight:'700', color: BRAND.charcoal, fontSize:'0.95em'}}>
                                Custom Tutorial Structure?
                            </label>
                        </div>
                    </div>
                )}

                {isEditMode && customTutorialStructure && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display:'block', marginBottom:'6px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal }}>
                            Key Terms Per Matching Page
                        </label>
                        <select className="dash-input" value={keyTermsPerMatchingPage} onChange={(e) => setKeyTermsPerMatchingPage(e.target.value)}>
                            <option value="auto">Default (auto)</option>
                            <option value="2">2 terms per panel</option>
                            <option value="1">1 term per panel</option>
                        </select>
                    </div>
                )}

                {isEditMode && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9em', fontWeight: '700', color: BRAND.charcoal }}>
                            Tutorial Font
                        </label>
                        <FontDropdown value={tutorialFont} onChange={setTutorialFont} />
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap:'10px' }}>
                    <button onClick={handleClose} className="btn-base" style={{background:'#F0F2F5', color: BRAND.charcoal}}>Cancel</button>
                    <button onClick={handleSubmit} className="btn-base btn-teal">{isEditMode ? 'Save Changes' : 'Add Student'}</button>
                </div>
            </div>
        </div>
    );
};


const ExistingEntityModal = ({
    isOpen,
    onClose,
    title,
    items = [],
    itemLabel,
    emptyMessage,
    loading = false,
    onConfirm,
    confirmLabel
}) => {
    const [selectedId, setSelectedId] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedId('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45, 64, 66, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="dash-card" style={{ padding: '24px', width: '520px', maxWidth: '95%', background: 'white', maxHeight: '90vh', overflowY: 'auto' }}>
                <h3 style={{ marginTop: 0, color: BRAND.charcoal }}>{title}</h3>

                {loading ? (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>Loading...</p>
                ) : items.length === 0 ? (
                    <p style={{ color: '#888', fontStyle: 'italic' }}>{emptyMessage}</p>
                ) : (
                    <div style={{ display: 'grid', gap: '8px', marginBottom: '14px' }}>
                        {items.map(item => (
                            <label key={item._id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', background: selectedId === item._id ? '#EAF7EE' : 'white' }}>
                                <input
                                    type="radio"
                                    name="existing-item"
                                    value={item._id}
                                    checked={selectedId === item._id}
                                    onChange={(e) => setSelectedId(e.target.value)}
                                    style={{ marginTop: '3px' }}
                                />
                                <div style={{ color: BRAND.charcoal }}>
                                    {itemLabel(item)}
                                </div>
                            </label>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button className="btn-base" onClick={onClose} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Cancel</button>
                    <button
                        className="btn-base btn-teal"
                        disabled={!selectedId || loading || items.length === 0}
                        onClick={() => onConfirm(selectedId)}
                        style={{ opacity: (!selectedId || loading || items.length === 0) ? 0.6 : 1 }}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

const getClassRoute = (scheduleId, routeNamespace = 'demo') => {
    if (routeNamespace === 'lite') return `/lite/profiles/${scheduleId}`;
    if (routeNamespace === 'standard') return `/students/${scheduleId}`;
    return `/demo/profiles/${scheduleId}`;
};

const DemoHomeworkScheduleDetail = ({ onLogout, user, routeNamespace = 'demo' }) => {
    const guideUserId = user?.uid || user?.hashedPassword || null;

    useEffect(() => {
        setGuideUser(guideUserId);
    }, [guideUserId]);
    const { scheduleId } = useParams();
    const navigate = useNavigate();
    const _uid = user?.uid;
    const spKey = _uid ? `studentProfileMode_${_uid}` : 'studentProfileMode';
    const raKey = _uid ? `readingAgeMode_${_uid}` : 'readingAgeMode';
    const [scheduleData, setScheduleData] = useState(null);
    const [lessonPlan, setLessonPlan] = useState(null); // +++ NEW STATE +++
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [modalState, setModalState] = useState({ isOpen: false, lessonNumber: null });

    const [students, setStudents] = useState([]);
    const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
    const [isExistingStudentModalOpen, setIsExistingStudentModalOpen] = useState(false);
    const [isExistingClassModalOpen, setIsExistingClassModalOpen] = useState(false);
    const [editScaffoldingModal, setEditScaffoldingModal] = useState({ isOpen: false, student: null });
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [editProfileModal, setEditProfileModal] = useState({ isOpen: false, profile: null });
    const [studentLibrary, setStudentLibrary] = useState([]);
    const [classLibrary, setClassLibrary] = useState([]);
    const [existingLibraryLoading, setExistingLibraryLoading] = useState(false);
    const [newClassModal, setNewClassModal] = useState({ isOpen: false, value: '', saving: false, error: '' });
    const [primaryLanguageOptions, setPrimaryLanguageOptions] = useState([]);
    const [languagesLoading, setLanguagesLoading] = useState(false);
    const [homeworkProgressStats, setHomeworkProgressStats] = useState({});
    const [homeworkActiveStudentCounts, setHomeworkActiveStudentCounts] = useState({});
    const [analyzedLessonTags, setAnalyzedLessonTags] = useState([]);
    const [analyzedParsedContent, setAnalyzedParsedContent] = useState({});
    const [preTestLessonTag, setPreTestLessonTag] = useState('');
    const [postTestLessonTag, setPostTestLessonTag] = useState('');
    const [generatedPrePostWorksheets, setGeneratedPrePostWorksheets] = useState({});
    const [prePostGeneratingType, setPrePostGeneratingType] = useState('');

    const [prePostErrorsByType, setPrePostErrorsByType] = useState({});
    const [printStudentModal, setPrintStudentModal] = useState({ isOpen: false, testType: '', selectedIds: [] });
    const registrationCode = user?.registrationCodeUsed || user?.user?.registrationCodeUsed || user?.data?.registrationCodeUsed;
    const normalizedRegistrationCode = normalizeRegistrationCode(registrationCode);
    const isWilliamFordUser = normalizedRegistrationCode === 'WILLIAM FORD R_2026';
    const PRE_POST_EMAIL_ALLOWLIST = ['wf@classlabs.co.uk', 'padom@williamfordschool.co.uk'];
    const isPrePostAllowlisted = PRE_POST_EMAIL_ALLOWLIST.includes((user?.email || '').trim().toLowerCase());
    const usesAggregateLessonAnalysis = usesCustomUploadPortal(registrationCode);

    const [guideConfig, setGuideConfig] = useState({ isOpen: false, mode: 'manual', autoStep: null });

    const ns = routeNamespace === 'lite' ? '/lite' : routeNamespace === 'demo' ? '/demo' : '';
    const dashboardRoute = `${ns}/dashboard`;
    const homeworkRoute = (homeworkId) => `${ns}/homework/${homeworkId}`;
    const [classNameModal, setClassNameModal] = useState({ isOpen: false, value: '', pendingAction: null, saving: false, error: '' });

    const isOakAcademy = !!scheduleData?.lessonPlanId?.isOakAcademy;
    const hasStudents = students.length > 0;
    const hasTutorialsCreated = (scheduleData?.homeworks?.length || 0) > 0;
    const hasAnyCompletedTutorial = Object.values(homeworkProgressStats).some(s => (Number(s.completedCount) || 0) > 0);

    // CL26 accounts are permanently locked to anonymous-profile mode.
    const isCL26 = (user?.registrationCodeUsed || '').trim() === 'CL26';
    // For Oak plans (shared library content), always use the current session's profile mode from
    // localStorage — stored plan value may be missing on plans created before the fix.
    // For regular plans, use the stored lesson plan value (set at creation time).
    const isSupplyTeacherMode = isCL26
        || (isOakAcademy
            ? localStorage.getItem(spKey) === 'supply_teacher'
            : lessonPlan?.studentProfileMode === 'supply_teacher');

    const showWilliamFordPrePostTest = routeNamespace === 'standard' && !isSupplyTeacherMode && isWilliamFordUser && isPrePostAllowlisted;

    const guideKey = (step) => `hs_${routeNamespace}_${isSupplyTeacherMode ? 'profiles' : 'students'}_${step}`;

    useEffect(() => {
        if (loading || !isProgressiveGuideLoginEligible(guideUserId)) return;
        if (!hasStudents && !hasShownThisSession(guideKey(1))) {
            markShownThisSession(guideKey(1));
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 1 });
        } else if (hasStudents && !hasTutorialsCreated && !hasShownThisSession(guideKey(3))) {
            markShownThisSession(guideKey(3));
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 3 });
        } else if (hasTutorialsCreated && !hasShownThisSession(guideKey(4))) {
            markShownThisSession(guideKey(4));
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 4 });
        } else if (hasAnyCompletedTutorial && !hasShownThisSession(guideKey(5))) {
            markShownThisSession(guideKey(5));
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 5 });
        }
    }, [loading, hasStudents, hasTutorialsCreated, hasAnyCompletedTutorial, routeNamespace, isSupplyTeacherMode, guideUserId]);

    const handleGuideClose = () => {
        setGuideConfig(prev => ({ ...prev, isOpen: false }));
    };

    const fetchSupportedLanguages = useCallback(async () => {
        setLanguagesLoading(true);
        try {
            const response = await translationAPI.getSupportedLanguages();
            const fetchedLanguages = Array.isArray(response.data?.languages) ? response.data.languages : [];
            setPrimaryLanguageOptions(fetchedLanguages);
        } catch (error) {
            console.error('Failed to fetch supported translation languages:', error);
            setPrimaryLanguageOptions([
                'Arabic',
                'Bengali',
                'Chinese (Mandarin)',
                'French',
                'Polish',
                'Portuguese',
                'Punjabi',
                'Romanian',
                'Spanish',
                'Urdu',
                'Other',
            ]);
        } finally {
            setLanguagesLoading(false);
        }
    }, []);

    const fetchExistingLibrary = useCallback(async () => {
        setExistingLibraryLoading(true);
        try {
            const [studentsRes, classesRes] = await Promise.all([
                studentAPI.getLibrary().catch(() => ({ data: [] })),
                studentAPI.getClasses().catch(() => ({ data: [] }))
            ]);

            const currentScheduleId = String(scheduleId);
            const filteredStudents = (studentsRes.data || []).filter(student => {
                const studentScheduleId = typeof student.homeworkScheduleId === 'object'
                    ? student.homeworkScheduleId?._id
                    : student.homeworkScheduleId;
                return String(studentScheduleId) !== currentScheduleId;
            });

            const filteredClasses = (classesRes.data || []).filter(c => String(c._id) !== currentScheduleId);

            setStudentLibrary(filteredStudents);
            setClassLibrary(filteredClasses);
        } finally {
            setExistingLibraryLoading(false);
        }
    }, [scheduleId]);

    const fetchData = useCallback(async () => {
        try {
            const [scheduleRes, studentsRes] = await Promise.all([
                homeworkScheduleAPI.getById(scheduleId),
                studentAPI.getForSchedule(scheduleId).catch(() => ({ data: [] })) 
            ]);
            
            setScheduleData(scheduleRes.data);
            setStudents(studentsRes.data);

            const homeworks = scheduleRes.data?.homeworks || [];
            if (homeworks.length > 0) {
                const [progressResponses, deliveryResponses, tutorialResponses] = await Promise.all([
                    Promise.all(
                        homeworks.map(hw =>
                            studentTutorialLinkAPI.getClassStats(hw._id)
                                .then(res => ({ id: hw._id, data: res.data }))
                                .catch(() => ({ id: hw._id, data: null }))
                        )
                    ),
                    Promise.all(
                        homeworks.map(hw =>
                            studentTutorialLinkAPI.getDeliveryStatus(hw._id)
                                .then(res => ({ id: hw._id, data: res.data }))
                                .catch(() => ({ id: hw._id, data: { statuses: {} } }))
                        )
                    ),
                    Promise.all(
                        homeworks.map(hw =>
                            studentTutorialAPI.getForHomework(hw._id)
                                .then(res => ({ id: hw._id, data: res.data || [] }))
                                .catch(() => ({ id: hw._id, data: [] }))
                        )
                    )
                ]);

                const progressMap = {};
                progressResponses.forEach(item => {
                    progressMap[item.id] = item.data || {
                        sentCount: 0,
                        completedCount: 0,
                        avgSubstantiveCompleted: 0,
                        avgKeyTermsCompleted: 0
                    };
                });
                setHomeworkProgressStats(progressMap);

                const tutorialsByHomework = {};
                tutorialResponses.forEach(({ id, data }) => {
                    tutorialsByHomework[id] = data || [];
                });

                const statusByHomework = {};
                deliveryResponses.forEach(({ id, data }) => {
                    statusByHomework[id] = data?.statuses || {};
                });

                const latestSentByStudent = {};
                homeworks.forEach(hw => {
                    const homeworkId = hw._id;
                    const tutorials = tutorialsByHomework[homeworkId] || [];
                    const deliveryMap = statusByHomework[homeworkId] || {};

                    tutorials.forEach(tutorial => {
                        const tutorialId = tutorial?._id?.toString();
                        const studentId = tutorial?.studentId?.toString?.() || tutorial?.studentId;
                        if (!tutorialId || !studentId) return;

                        const status = deliveryMap[tutorialId];
                        if (!status?.sent) return;

                        const sentAtMs = status?.sentAt ? new Date(status.sentAt).getTime() : 0;
                        const existing = latestSentByStudent[studentId];
                        if (!existing || sentAtMs > existing.sentAtMs) {
                            latestSentByStudent[studentId] = { homeworkId, sentAtMs };
                        }
                    });
                });

                const activeCountMap = {};
                Object.values(latestSentByStudent).forEach(({ homeworkId }) => {
                    activeCountMap[homeworkId] = (activeCountMap[homeworkId] || 0) + 1;
                });
                setHomeworkActiveStudentCounts(activeCountMap);
            } else {
                setHomeworkProgressStats({});
                setHomeworkActiveStudentCounts({});
            }
            // +++ NEW: Fetch Lesson Plan details +++
            if (scheduleRes.data?.lessonPlanId) {
                try {
                    const lpId = typeof scheduleRes.data.lessonPlanId === 'object'
                        ? scheduleRes.data.lessonPlanId._id
                        : scheduleRes.data.lessonPlanId;

                    const [lpRes, analysisRes] = await Promise.all([
                        lessonPlansAPI.getById(lpId),
                        lessonAnalysisAPI.getStatus(lpId).catch(() => ({ data: null }))
                    ]);

                    setLessonPlan(lpRes.data);

                    const parsed = analysisRes?.data?.parsedContent || {};
                    const tags = Object.keys(parsed)
                        .map(key => key.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()));
                    setAnalyzedLessonTags(tags);
                    setAnalyzedParsedContent(parsed);

                    const rawCache = analysisRes?.data?.prePostCache || {};
                    const hydrated = {};
                    for (const [testType, cached] of Object.entries(rawCache)) {
                        if (cached?.assessment) {
                            hydrated[testType] = normalizeGeneratedAssessment({
                                testType,
                                assessment: cached.assessment,
                                images: cached.images || []
                            });
                        }
                    }
                    if (Object.keys(hydrated).length > 0) {
                        setGeneratedPrePostWorksheets(hydrated);
                    }
                } catch (lpErr) {
                    console.error("Failed to load Lesson Plan details", lpErr);
                    setAnalyzedLessonTags([]);
                    setAnalyzedParsedContent({});
                }
            } else {
                setAnalyzedLessonTags([]);
                setAnalyzedParsedContent({});
            }

        } catch (err) {
            setError('Failed to fetch class details.');
        } finally {
            setLoading(false);
        }
    }, [scheduleId]);

    useEffect(() => {
        fetchData();
        fetchSupportedLanguages();
        fetchExistingLibrary();
    }, [fetchData, fetchSupportedLanguages, fetchExistingLibrary]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchData();
        }, 15000);

        return () => clearInterval(interval);
    }, [fetchData]);


    const lessonObjectiveOptionsByTag = useMemo(() => {
        const result = {};
        const parsed = analyzedParsedContent || {};

        Object.keys(parsed).forEach((tag) => {
            const normalizedTag = tag.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
            const objectives = Array.isArray(parsed?.[tag]?.objectives) ? parsed[tag].objectives : [];
            result[normalizedTag] = objectives
                .map(item => ({
                    objective: (item?.objective || '').toString().trim(),
                    misconception: (item?.misconception || '').toString().trim()
                }))
                .filter(item => item.objective);
        });

        return result;
    }, [analyzedParsedContent]);

    const lessonTitlesByTag = useMemo(() => {
        const result = {};
        const parsed = analyzedParsedContent || {};
        const entries = parsed instanceof Map
            ? Array.from(parsed.entries())
            : Object.entries(parsed);

        entries.forEach(([tag, value]) => {
            const normalizedTag = toTitleCaseTag(tag);
            const rawTitle = (value?.title || '').toString().trim();
            const looksGeneric = /^lesson\s*\d+\s*:??\s*$/i.test(rawTitle);

            if (rawTitle && !looksGeneric) {
                result[normalizedTag] = rawTitle;
                return;
            }

            const topic = deriveLessonTopicFromEntry(value, normalizedTag);
            if (topic) {
                result[normalizedTag] = `${normalizedTag}: ${topic}`;
            }
        });

        return result;
    }, [analyzedParsedContent]);

    const prePostWorksheetLessonEntries = useMemo(() => {
        const parsed = analyzedParsedContent || {};
        const entries = parsed instanceof Map
            ? Array.from(parsed.entries())
            : Object.entries(parsed);

        return entries
            .map(([tag, entry]) => {
                const lessonTag = toTitleCaseTag(tag);
                const lessonTitle = lessonTitlesByTag[lessonTag]
                    || lessonTitlesByTag[lessonTag.toLowerCase()]
                    || lessonTitlesByTag[lessonTag.toUpperCase()]
                    || '';
                const topic = getWorksheetTopic(entry, lessonTag, lessonTitle);
                return { lessonTag, entry, lessonTitle, topic };
            })
            .sort((a, b) => {
                const numA = extractLessonNumberFromTag(a.lessonTag);
                const numB = extractLessonNumberFromTag(b.lessonTag);
                if (numA && numB) return numA - numB;
                if (numA) return -1;
                if (numB) return 1;
                return a.lessonTag.localeCompare(b.lessonTag);
            });
    }, [analyzedParsedContent, lessonTitlesByTag]);

    const prePostModuleTitle = scheduleData?.lessonPlanId?.title || lessonPlan?.title || 'All lessons';
    const prePostWorksheetOptions = useMemo(() => {
        if (prePostWorksheetLessonEntries.length === 0) return [];
        return [{
            value: 'full-module',
            label: `All lessons (${prePostWorksheetLessonEntries.length}) — ${prePostModuleTitle}`
        }];
    }, [prePostWorksheetLessonEntries.length, prePostModuleTitle]);

    useEffect(() => {
        if (prePostWorksheetOptions.length === 0) {
            setPreTestLessonTag('');
            setPostTestLessonTag('');
            return;
        }

        const defaultValue = prePostWorksheetOptions[0].value;
        const hasPreSelection = prePostWorksheetOptions.some(option => option.value === preTestLessonTag);
        const hasPostSelection = prePostWorksheetOptions.some(option => option.value === postTestLessonTag);
        if (!hasPreSelection) setPreTestLessonTag(defaultValue);
        if (!hasPostSelection) setPostTestLessonTag(defaultValue);
    }, [prePostWorksheetOptions, preTestLessonTag, postTestLessonTag]);

    const getPrePostWorksheet = useCallback((testType) => {
        if (generatedPrePostWorksheets[testType]) return generatedPrePostWorksheets[testType];
        if (prePostWorksheetLessonEntries.length === 0) return null;
        return buildPrePostWorksheet({
            testType,
            lessons: prePostWorksheetLessonEntries,
            moduleTitle: prePostModuleTitle
        });
    }, [generatedPrePostWorksheets, prePostWorksheetLessonEntries, prePostModuleTitle]);

    const generatePrePostWorksheet = useCallback(async (testType) => {
        const lessonPlanId = typeof scheduleData?.lessonPlanId === 'object'
            ? scheduleData?.lessonPlanId?._id
            : scheduleData?.lessonPlanId;

        if (!lessonPlanId) {
            throw new Error('Lesson plan is not ready yet.');
        }

        const assessmentRes = await lessonAnalysisAPI.generatePrePostAssessment(lessonPlanId, testType);
        const assessment = assessmentRes.data?.assessment;
        if (!assessment) {
            throw new Error('Assessment content was not generated.');
        }

        let images = [];
        try {
            const imagesRes = await lessonAnalysisAPI.generatePrePostAssessmentImages(lessonPlanId, assessment, testType);
            images = imagesRes.data?.images || [];
        } catch (imageErr) {
            console.error('Pre/post visual image generation failed (non-fatal):', imageErr);
        }
        const worksheet = normalizeGeneratedAssessment({ testType, assessment, images });
        setGeneratedPrePostWorksheets(prev => ({ ...prev, [testType]: worksheet }));
        return worksheet;
    }, [scheduleData?.lessonPlanId]);

    const handleGeneratePrePostWorksheet = useCallback(async (testType) => {
        setPrePostErrorsByType(prev => ({ ...prev, [testType]: '' }));
        setPrePostGeneratingType(testType);

        try {
            await generatePrePostWorksheet(testType);
        } catch (err) {
            console.error('Failed to generate pre/post worksheet:', err);
            setPrePostErrorsByType(prev => ({ ...prev, [testType]: getPrePostGenerationMessage(err) }));
        } finally {
            setPrePostGeneratingType('');
        }
    }, [generatePrePostWorksheet]);

    const handleOpenPrintModal = useCallback((testType) => {
        const allIds = students.map(s => s._id || s.id).filter(Boolean);
        setPrintStudentModal({ isOpen: true, testType, selectedIds: allIds });
    }, [students]);

    const handlePrintPrePostWorksheet = useCallback((testType, selectedStudents) => {
        setPrePostErrorsByType(prev => ({ ...prev, [testType]: '' }));
        setPrintStudentModal({ isOpen: false, testType: '', selectedIds: [] });

        const worksheet = generatedPrePostWorksheets[testType];
        if (!worksheet) {
            alert('Please generate this worksheet before printing.');
            return;
        }

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow pop-ups for ClassCompass so the worksheet can open for printing.');
            return;
        }

        printWindow.opener = null;
        printWindow.document.open();
        printWindow.document.write(createWorksheetPrintHtml(worksheet, selectedStudents));
        printWindow.document.close();
    }, [generatedPrePostWorksheets]);

    const preWorksheetPreview = getPrePostWorksheet('Pre-Test');
    const postWorksheetPreview = getPrePostWorksheet('Post-Test');

    const handleCreateHomework = async (homeworkData) => {
        try {
            if (isOakAcademy) {
                // For OA plans, activate the pre-existing stub using ContentLibrary content
                const stub = scheduleData.homeworks?.find(h => h.lessonNumber === homeworkData.lessonNumber);
                if (!stub) { alert('Could not find lesson stub.'); return; }
                const res = await contentLibraryAPI.activateOakLesson({
                    homeworkId: stub._id,
                    studentIds: homeworkData.studentIds,
                    durationMinutes: homeworkData.durationMinutes,
                });
                setModalState({ isOpen: false, lessonNumber: null });
                navigate(homeworkRoute(res.data.homeworkId));
                return;
            }
            const res = await homeworkAPI.create({
                ...homeworkData,
                homeworkScheduleId: scheduleId,
                lessonPlanId: scheduleData.lessonPlanId._id,
            });
            const newHomeworkId = res?.data?._id;
            setModalState({ isOpen: false, lessonNumber: null });
            if (newHomeworkId) {
                navigate(homeworkRoute(newHomeworkId));
                return;
            }
            fetchData();
        } catch (error) {
            alert('Failed to create homework.');
        }
    };

    // For Oak Academy schedules: prompt to name the class before adding students
    const handleOakAddStudent = (action) => {
        if (isOakAcademy && scheduleData?.name === 'Unnamed Class') {
            setClassNameModal({ isOpen: true, value: '', pendingAction: action, saving: false, error: '' });
        } else {
            action();
        }
    };

    const handleClassNameConfirm = async () => {
        if (!classNameModal.value.trim()) {
            setClassNameModal(prev => ({ ...prev, error: 'Please enter a class name.' }));
            return;
        }
        setClassNameModal(prev => ({ ...prev, saving: true, error: '' }));
        try {
            const updated = await homeworkScheduleAPI.rename(scheduleId, classNameModal.value.trim());
            setScheduleData(prev => ({ ...prev, name: updated.data.name }));
            const action = classNameModal.pendingAction;
            setClassNameModal({ isOpen: false, value: '', pendingAction: null, saving: false, error: '' });
            action?.();
        } catch {
            setClassNameModal(prev => ({ ...prev, saving: false, error: 'Failed to save class name.' }));
        }
    };

    const handleDeleteSchedule = async () => {
        if (window.confirm('Are you sure you want to delete this schedule and all its homeworks?')) {
            try {
                await homeworkScheduleAPI.deleteById(scheduleId);
                alert('Schedule deleted successfully.');
                navigate(dashboardRoute);
            } catch (err) {
                alert('Failed to delete schedule.');
            }
        }
    };

    const handleCreateStudent = async (studentData) => {
        try {
            await studentAPI.create({
                ...studentData,
                homeworkScheduleId: scheduleId
            });
            setIsStudentModalOpen(false);
            fetchData(); 
        } catch (err) {
            alert('Failed to add student.');
        }
    };

    const handleUpdateStudentScaffolding = async (studentId, scaffoldingData) => {
        try {
            await studentAPI.updateScaffolding(studentId, scaffoldingData);

            // Telemetry should never block student settings updates.
            void trackUserEvent('STUDENT_MANAGEMENT', 'student_profile_updated', {
                studentId,
                eal: Boolean(scaffoldingData?.eal),
                senConditions: Array.isArray(scaffoldingData?.senConditions)
                    ? scaffoldingData.senConditions
                    : (scaffoldingData?.senCondition && scaffoldingData.senCondition !== 'None' ? [scaffoldingData.senCondition] : []),
            });
            setEditScaffoldingModal({ isOpen: false, student: null });
            fetchData();
            fetchExistingLibrary();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update scaffolding.');
        }
    };

    const handleAddExistingStudent = async (sourceStudentId) => {
        try {
            await studentAPI.addExistingStudentToSchedule(scheduleId, sourceStudentId);
            setIsExistingStudentModalOpen(false);
            fetchData();
            fetchExistingLibrary();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to add existing student.');
        }
    };

    const handleAddExistingClass = async (sourceScheduleId) => {
        try {
            await studentAPI.addExistingClassToSchedule(scheduleId, sourceScheduleId);

            // For OA plans: rename the schedule to the source class name
            if (isOakAcademy && scheduleData?.name === 'Unnamed Class') {
                const sourceClass = classLibrary.find(c => String(c._id) === String(sourceScheduleId));
                if (sourceClass?.name) {
                    const updated = await homeworkScheduleAPI.rename(scheduleId, sourceClass.name);
                    setScheduleData(prev => ({ ...prev, name: updated.data.name }));
                }
            }

            setIsExistingClassModalOpen(false);
            await fetchData();
            fetchExistingLibrary();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to add existing class.');
        }
    };

    const handleCreateNewClass = async () => {
        const className = String(newClassModal.value || '').trim();
        if (!className) {
            setNewClassModal(prev => ({ ...prev, error: 'Please enter a class name.' }));
            return;
        }
        const lessonPlanId = typeof scheduleData?.lessonPlanId === 'object'
            ? scheduleData?.lessonPlanId?._id
            : scheduleData?.lessonPlanId;
        if (!lessonPlanId) {
            setNewClassModal(prev => ({ ...prev, error: 'Lesson plan information is missing. Please refresh and try again.' }));
            return;
        }
        setNewClassModal(prev => ({ ...prev, saving: true, error: '' }));
        try {
            const created = await homeworkScheduleAPI.create({ name: className, lessonPlanId });
            const newScheduleId = created?.data?._id;
            setNewClassModal({ isOpen: false, value: '', saving: false, error: '' });
            if (newScheduleId) {
                navigate(getClassRoute(newScheduleId, routeNamespace));
                return;
            }
            alert('Class created successfully, but opening it failed. Please find it on your dashboard.');
        } catch (err) {
            setNewClassModal(prev => ({ ...prev, saving: false, error: err?.response?.data?.error || 'Failed to create new class.' }));
        }
    };

    const handleDeleteStudent = async (studentId) => {
        if (window.confirm('Are you sure you want to delete this student?')) {
            try {
                await studentAPI.deleteById(studentId);
                if (lessonPlan?.studentProfileMode === 'supply_teacher') {
                    await studentAPI.renumberSupplyTeacher(scheduleId);
                }
                fetchData();
            } catch (err) {
                alert('Failed to delete student.');
            }
        }
    };

    const profileGroups = isSupplyTeacherMode ? (() => {
        const groups = {};
        students.forEach(s => {
            const key = getProfileKey(s);
            if (!groups[key]) {
                groups[key] = { key, label: getProfileLabel(s), students: [], representative: s };
            }
            groups[key].students.push(s);
        });
        return Object.values(groups).sort((a, b) => {
            if (a.key === '__mainstream__') return -1;
            if (b.key === '__mainstream__') return 1;
            return a.label.localeCompare(b.label);
        });
    })() : [];

    const handleAddProfile = async (profileData, count) => {
        try {
            await studentAPI.bulkCreateProfile(scheduleId, profileData, count);
            setIsProfileModalOpen(false);
            fetchData();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to add profile.');
        }
    };

    const handleEditProfile = async (profileData, newCount) => {
        try {
            const group = editProfileModal.profile;
            const studentIds = group.students.map(s => s._id);
            await studentAPI.bulkUpdateScaffolding(scheduleId, studentIds, profileData, newCount);
            setEditProfileModal({ isOpen: false, profile: null });
            fetchData();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update profile.');
        }
    };

    const handleDeleteProfile = async (group) => {
        if (window.confirm(`Delete all ${group.students.length} "${group.label}" student${group.students.length !== 1 ? 's' : ''}?`)) {
            try {
                const studentIds = group.students.map(s => s._id);
                await studentAPI.bulkDelete(scheduleId, studentIds);
                fetchData();
            } catch (err) {
                alert(err.response?.data?.error || 'Failed to delete profile.');
            }
        }
    };

    if (loading) return <div style={{padding:'40px', textAlign:'center', fontFamily:'Inter, sans-serif'}}>Loading schedule...</div>;
    if (error) return <p style={{ color: BRAND.danger, padding:'20px', fontWeight:'bold' }}>{error}</p>;

    const numberOfLessons = Math.max(
        scheduleData?.lessonPlanId?.numberOfLessons || 0,
        ...(scheduleData?.homeworks || []).map(h => h.lessonNumber || 0),
        getMaxLessonNumber(analyzedLessonTags, scheduleData?.availableTags || [])
    );

    return (
        <>
        <div style={{
            fontFamily: "'Inter', sans-serif",
            backgroundColor: BRAND.cream,
            minHeight: '100vh',
            color: BRAND.charcoal,
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* GLOBAL STYLES */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
                    
                    h1, h2, h3, h4 { font-family: 'Nunito', sans-serif; }

                    .pattern-bg {
                        background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                        background-size: 60px 60px;
                        background-position: 0 0, 30px 30px;
                        opacity: 0.1;
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0; 
                        z-index: 0;
                        pointer-events: none;
                        mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                        -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                    }

                    .dash-card {
                        background: ${BRAND.white};
                        border-radius: 16px;
                        border: 2px solid ${BRAND.charcoal};
                        box-shadow: 4px 4px 0px rgba(45, 64, 66, 0.1);
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                        overflow: hidden;
                    }
                    
                    .dash-input {
                        padding: 12px 16px;
                        border-radius: 10px;
                        border: 2px solid #E0E0E0;
                        background-color: #F8F9FA;
                        width: 100%;
                        box-sizing: border-box;
                        transition: all 0.2s;
                        outline: none;
                        font-family: 'Inter', sans-serif;
                        font-weight: 500;
                        font-size: 0.95rem;
                        color: ${BRAND.charcoal};
                    }
                    .dash-input:focus {
                        background-color: white;
                        border-color: ${BRAND.teal};
                    }

                    .btn-base {
                        border: none;
                        border-radius: 10px;
                        padding: 10px 20px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        cursor: pointer;
                        transition: all 0.1s ease;
                        font-size: 0.95rem;
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        border-bottom: 4px solid rgba(0,0,0,0.1); 
                    }
                    .btn-base:active {
                        transform: translateY(2px);
                        border-bottom: 2px solid rgba(0,0,0,0.1);
                        margin-bottom: 2px;
                    }
                    .btn-base:disabled {
                        background-color: #D1D5DB;
                        color: #6B7280;
                        border-bottom: 4px solid #9CA3AF;
                        cursor: not-allowed;
                        transform: none;
                        margin-bottom: 0;
                        box-shadow: none;
                    }
                    
                    .btn-teal { background-color: ${BRAND.teal}; color: white; }
                    .btn-coral { background-color: ${BRAND.coral}; color: white; }
                    @keyframes cc-spin { to { transform: rotate(360deg); } }
                    
                    .btn-white {
                        background-color: white;
                        color: ${BRAND.charcoal};
                        border: 2px solid ${BRAND.charcoal};
                        border-bottom: 4px solid ${BRAND.charcoal};
                    }
                    .btn-white:active { border-bottom: 2px solid ${BRAND.charcoal}; }
                    
                    .btn-danger {
                        background-color: white;
                        color: ${BRAND.danger};
                        border: 2px solid ${BRAND.danger};
                        border-bottom: 4px solid ${BRAND.danger};
                    }
                    .btn-danger:active { border-bottom: 2px solid ${BRAND.danger}; }

                    .btn-logout {
                        background-color: white;
                        color: ${BRAND.charcoal};
                        border: 2px solid ${BRAND.charcoal};
                        border-bottom: 4px solid ${BRAND.charcoal};
                        padding: 6px 16px;
                        font-size: 0.85rem;
                    }
                    .btn-logout:active {
                        border-bottom: 2px solid ${BRAND.charcoal};
                    }
                    .btn-logout:hover {
                        background-color: #F9F9F9;
                    }

                    /* COLLAPSIBLE INPUTS */
                    details > summary { list-style: none; }
                    details > summary::-webkit-details-marker { display: none; }
                    
                    .inputs-summary {
                        display: flex; alignItems: center; cursor: pointer; outline: none;
                        font-size: 0.8em; color: #888; text-transform: uppercase; 
                        letter-spacing: 1px; font-weight: 700; margin-bottom: 10px;
                    }
                    .inputs-chevron {
                        display: inline-block; margin-right: 8px; transition: transform 0.2s ease;
                    }
                    details[open] > .inputs-summary .inputs-chevron { transform: rotate(90deg); }

                    .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
                    .toggle-switch input { opacity: 0; width: 0; height: 0; }
                    .slider {
                        position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
                        background-color: #E0E0E0; transition: .3s; border-radius: 34px;
                    }
                    .slider:before {
                        position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px;
                        background-color: white; transition: .3s; border-radius: 50%;
                    }
                    input:checked + .slider { background-color: ${BRAND.teal}; }
                    input:checked + .slider:before { transform: translateX(20px); }

                    @media (max-width: 900px) {
                        .user-guide-label { display: none; }
                    }
                `}
            </style>

            <div className="pattern-bg"></div>

            <div style={{ position: 'relative', zIndex: 1, padding: '40px 20px', maxWidth: '1000px', margin: '0 auto' }}>
                {/* HEADER */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <Link to={dashboardRoute} style={{textDecoration:'none'}}>
                        <button className="btn-base btn-white">← Back to Dashboard</button>
                    </Link>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                        <button onClick={handleDeleteSchedule} className="btn-base btn-danger">Delete Schedule</button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label className="user-guide-label" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#2D4042', fontWeight: '800', fontFamily: 'Nunito, sans-serif', margin: 0 }}>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked={guideConfig.isOpen} onChange={(e) => setGuideConfig({ isOpen: e.target.checked, mode: 'manual', autoStep: null })} />
                                    <span className="slider"></span>
                                </label>
                                User Guide
                            </label>
                            <button onClick={onLogout} className="btn-base btn-logout">
                                Log Out
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{marginBottom:'30px'}}>
                    <h2 style={{ margin: '0 0 4px 0', color: '#5D7375', fontWeight: '700', fontSize: '1.25rem' }}>
                        Topic: {scheduleData?.lessonPlanId?.title || lessonPlan?.title || 'Untitled Module'}
                    </h2>
                    <h1 style={{ margin: '0 0 5px 0', color: BRAND.charcoal, fontWeight: '800', fontSize: '2.5rem' }}>
                        Class: {scheduleData?.name}
                    </h1>
                    {/* +++ UPDATED LABEL +++ */}
                    <p style={{margin:0, color: '#5D7375', fontSize:'1.1rem', fontWeight:'500'}}>Manage tutorials and students for this schedule.</p>
                </div>

                {/* INFO CARD */}
                <div className="dash-card" style={{ padding: '25px', marginBottom: '30px', background: 'white', display:'flex', flexDirection:'column' }}>
                    <div style={{ display:'flex', gap:'30px' }}>
                        <div>
                            <span style={{display:'block', fontSize:'0.8em', color: '#888', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700'}}>Subject</span>
                            <strong style={{color: BRAND.charcoal, fontSize:'1.2em', fontWeight:'800'}}>{scheduleData?.subject}</strong>
                        </div>
                        <div>
                            <span style={{display:'block', fontSize:'0.8em', color: '#888', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700'}}>Year Group</span>
                            <strong style={{color: BRAND.charcoal, fontSize:'1.2em', fontWeight:'800'}}>{scheduleData?.yearGroup}</strong>
                        </div>
                        <div>
                            <span style={{display:'block', fontSize:'0.8em', color: '#888', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'700'}}>Based On</span>
                            <strong style={{color: BRAND.charcoal, fontSize:'1.2em', fontWeight:'800'}}>{scheduleData?.lessonPlanId?.title}</strong>
                        </div>
                    </div>

                    {/* +++ NEW INPUTS SECTION (COLLAPSIBLE) +++ */}
                    {lessonPlan?.documents && lessonPlan.documents.length > 0 && (
                        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '2px solid #eee', width: '100%' }}>
                            <details>
                                <summary className="inputs-summary">
                                    <span className="inputs-chevron">▶</span> 
                                    Inputs ({lessonPlan.documents.length})
                                </summary>
                                <div style={{display:'flex', flexWrap:'wrap', gap:'10px', marginTop:'5px'}}>
                                    {lessonPlan.documents.map((doc, idx) => (
                                        <div key={idx} style={{
                                            background: '#F8F9FA', 
                                            border: '1px solid #E0E0E0', 
                                            borderRadius: '6px', 
                                            padding: '6px 12px', 
                                            fontSize: '0.85em', 
                                            color: BRAND.charcoal,
                                            display: 'flex', alignItems: 'center', gap: '8px'
                                        }}>
                                            <span style={{fontWeight:'700'}}>{doc.originalName || doc.name}</span>
                                            <span style={{fontSize:'0.75em', color:'#555', background:'#E0E0E0', padding:'2px 6px', borderRadius:'4px', textTransform:'uppercase', fontWeight:'600'}}>
                                                {doc.lesson_tag || 'General'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        </div>
                    )}
                </div>

                {/* STUDENTS CARD */}
                <div className="dash-card" style={{ padding: '25px', marginBottom: '40px', background: 'white' }}>
                    {isSupplyTeacherMode ? (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: `2px solid ${BRAND.charcoal}`, paddingBottom: '15px' }}>
                                <h3 style={{ margin: 0, color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontWeight: '800' }}>Profiles</h3>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    <button className="btn-base" onClick={() => setIsExistingClassModalOpen(true)} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>+ Existing Class</button>
                                    <button className="btn-base" onClick={() => setNewClassModal({ isOpen: true, value: '', saving: false, error: '' })} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>+ New Class</button>
                                    <button className="btn-base btn-teal" data-guide="hs-add-student" onClick={() => handleOakAddStudent(() => setIsProfileModalOpen(true))}>+ Add Profile</button>
                                </div>
                            </div>
                            {profileGroups.length > 0 ? (
                                <div style={{ border: '2px solid #E0E0E0', borderRadius: '12px', overflow: 'hidden' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', background: '#F4F7F6', borderBottom: '2px solid #E0E0E0', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '800', color: '#666' }}>
                                        <div style={{ padding: '12px 14px' }}>Profile</div>
                                        <div style={{ padding: '12px 14px' }}>Count</div>
                                        <div style={{ padding: '12px 14px', textAlign: 'center' }}>Actions</div>
                                    </div>
                                    {profileGroups.map((group, idx) => (
                                        <div key={group.key} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', borderBottom: idx === profileGroups.length - 1 ? 'none' : '1px solid #EAEAEA', background: idx % 2 === 0 ? 'white' : '#FCFCFC', alignItems: 'center' }}>
                                            <div style={{ padding: '14px', fontWeight: '700', color: BRAND.charcoal }}>{group.label}</div>
                                            <div style={{ padding: '14px' }}>
                                                <span style={{ background: '#F0F9FF', border: '1px solid #BDE0FE', borderRadius: '999px', padding: '4px 12px', fontWeight: '800', color: '#005580', fontSize: '0.9em', whiteSpace: 'nowrap' }}>
                                                    × {group.students.length}
                                                </span>
                                            </div>
                                            <div style={{ padding: '14px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                <button className="btn-base" data-guide={idx === 0 ? 'hs-edit-student' : undefined} onClick={() => setEditProfileModal({ isOpen: true, profile: group })} style={{ background: '#F0F2F5', color: BRAND.charcoal, padding: '6px 10px', fontSize: '0.75rem' }}>Edit</button>
                                                <button onClick={() => handleDeleteProfile(group)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4em', color: '#CCC', transition: 'color 0.2s', lineHeight: 1 }}
                                                    onMouseOver={e => e.target.style.color = BRAND.danger}
                                                    onMouseOut={e => e.target.style.color = '#CCC'}>
                                                    &times;
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#888', fontStyle: 'italic', border: '2px dashed #eee', borderRadius: '10px' }}>
                                    No profiles added yet. Add profiles to generate personalised tutorials.
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: `2px solid ${BRAND.charcoal}`, paddingBottom: '15px', gap: '10px', flexWrap: 'wrap' }}>
                                <h3 style={{ margin: 0, color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontWeight: '800' }}>Class List</h3>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <button className="btn-base" onClick={() => setIsExistingClassModalOpen(true)} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>+ Existing Class</button>
                                    <button className="btn-base" onClick={() => handleOakAddStudent(() => setIsExistingStudentModalOpen(true))} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>+ Existing Student</button>
                                    <button className="btn-base btn-teal" data-guide="hs-add-student" onClick={() => handleOakAddStudent(() => setIsStudentModalOpen(true))}>+ New Student</button>
                                </div>
                            </div>
                            {students.length > 0 ? (
                                <div style={{ border: '2px solid #E0E0E0', borderRadius: '12px', overflow: 'hidden' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.9fr 1.9fr 0.8fr 1.1fr', background: '#F4F7F6', borderBottom: '2px solid #E0E0E0', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '800', color: '#666' }}>
                                        <div style={{ padding: '12px 14px' }}>Student Name</div>
                                        <div style={{ padding: '12px 10px' }}>Age</div>
                                        <div style={{ padding: '12px 10px' }}>RA</div>
                                        <div style={{ padding: '12px 10px' }}>SEN</div>
                                        <div style={{ padding: '12px 10px' }}>EAL</div>
                                        <div style={{ padding: '12px 10px', textAlign: 'center' }}>Actions</div>
                                    </div>
                                    {students.map((student, idx) => (
                                        <div key={student._id} style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.9fr 1.9fr 0.8fr 1.1fr', borderBottom: idx === students.length - 1 ? 'none' : '1px solid #EAEAEA', background: idx % 2 === 0 ? 'white' : '#FCFCFC', alignItems: 'center' }}>
                                            <div style={{ padding: '12px 14px', fontWeight: '700', color: BRAND.charcoal }}>
                                                {student.name}
                                                {student.email && (
                                                    <div style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: '600', marginTop: '2px' }}>{student.email}</div>
                                                )}
                                                {student.audioNarrationEnabled && (
                                                    <span style={{ marginLeft: '10px', fontSize: '0.7em', background: BRAND.success, color: 'white', padding: '2px 8px', borderRadius: '10px', verticalAlign: 'middle', fontWeight: '800' }}>AUDIO</span>
                                                )}
                                            </div>
                                            <div style={{ padding: '12px 10px', color: '#555', fontWeight: '600' }}>{student.age}</div>
                                            <div style={{ padding: '12px 10px', color: '#555', fontWeight: '600' }}>{student.readingAgeLabel || student.readingAge}</div>
                                            <div style={{ padding: '12px 10px', color: student.senCondition !== 'None' ? BRAND.danger : '#666', fontWeight: student.senCondition !== 'None' ? '700' : '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={student.senCondition}>
                                                {student.senCondition}
                                            </div>
                                            <div style={{ padding: '12px 10px', color: student.eal ? BRAND.teal : '#666', fontWeight: student.eal ? '800' : '500' }}>
                                                {student.eal ? 'Yes' : 'No'}
                                            </div>
                                            <div style={{ padding: '12px 10px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                                <button className="btn-base" data-guide="hs-edit-student" onClick={() => setEditScaffoldingModal({ isOpen: true, student })} style={{ background: '#F0F2F5', color: BRAND.charcoal, padding: '6px 10px', fontSize: '0.75rem' }} title="Edit Scaffolding">Edit</button>
                                                <button onClick={() => handleDeleteStudent(student._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4em', color: '#CCC', transition: 'color 0.2s', lineHeight: 1 }}
                                                    onMouseOver={e => e.target.style.color = BRAND.danger}
                                                    onMouseOut={e => e.target.style.color = '#CCC'}
                                                    title="Remove Student">&times;</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#888', fontStyle: 'italic', border: '2px dashed #eee', borderRadius: '10px' }}>
                                    No students added yet. Add students to generate personalized tutorials.
                                </div>
                            )}
                        </>
                    )}
                </div>

                <h2 style={{margin:'0 0 20px 0', color: BRAND.charcoal, fontFamily:'Nunito, sans-serif', fontWeight:'800'}}>Lesson Schedule</h2>

                <div data-guide="hs-lesson-schedule" style={{display:'grid', gap:'15px'}}>
                    {Array.from({ length: numberOfLessons }, (_, i) => {
                        const lessonNum = i + 1;
                        const allLessonHomeworks = (scheduleData.homeworks?.filter(h => h.lessonNumber === lessonNum) || []).sort((a, b) => a.homeworkNumber - b.homeworkNumber);
                        // For OA: tutorial chips only show for activated stubs (those with students)
                        const existingHomeworks = isOakAcademy
                            ? allLessonHomeworks.filter(h => h.students?.length > 0)
                            : allLessonHomeworks;
                        
                        const activeStudentsForLesson = existingHomeworks.reduce((sum, hw) => sum + (homeworkActiveStudentCounts[hw._id] || 0), 0);
                        const lessonTag = `Lesson ${lessonNum}`;
                        const canCreateTutorial = isOakAcademy
                            ? allLessonHomeworks.length > 0 && hasStudents
                            : analyzedLessonTags.some(tag => tag.toLowerCase() === lessonTag.toLowerCase());
                        const lessonTitle = lessonTitlesByTag[lessonTag]
                            || lessonTitlesByTag[lessonTag.toLowerCase()]
                            || lessonTitlesByTag[lessonTag.toUpperCase()]
                            || '';
                        const lessonLabel = usesAggregateLessonAnalysis
                            ? (lessonTitle
                                ? (lessonTitle.toLowerCase().startsWith(`${lessonTag.toLowerCase()}:`)
                                    ? lessonTitle
                                    : `${lessonTag}: ${lessonTitle}`)
                                : lessonTag)
                            : null;

                        return (
                            <div key={lessonNum} className="dash-card" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '20px', justifyContent:'space-between', background: activeStudentsForLesson > 0 ? '#EAF7EE' : 'white', border: activeStudentsForLesson > 0 ? `2px solid ${BRAND.success}` : '2px solid transparent' }}>
                                <div style={{display:'flex', alignItems:'center', gap:'20px', flexGrow:1}}>
                                    
                                    {/* RESIZED LESSON SQUARE */}
                                    <div style={{ 
                                        minWidth: '60px', height:'60px', background: '#F4F7F6', borderRadius: '12px', 
                                        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                                        color: '#888', border: '2px solid #E0E0E0'
                                    }}>
                                        <span style={{fontSize:'0.6em', fontWeight:'800', textTransform:'uppercase', letterSpacing:'1px'}}>Lesson</span>
                                        <span style={{fontSize:'1.5em', fontWeight:'800', color: BRAND.charcoal, lineHeight:'1'}}>{lessonNum}</span>
                                    </div>
                                    
                                    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {(usesAggregateLessonAnalysis || isOakAcademy) && (() => {
                                            const oaTitle = isOakAcademy
                                                ? (allLessonHomeworks[0]?.sourceLessonTags?.find(t => !t.startsWith('__preview__:')) || '')
                                                : null;
                                            const displayTitle = oaTitle || lessonLabel;
                                            return displayTitle ? (
                                                <div style={{ fontWeight: '700', color: BRAND.charcoal, fontSize: '0.95rem', lineHeight: 1.3 }}>
                                                    {displayTitle}
                                                </div>
                                            ) : null;
                                        })()}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                            {isOakAcademy && !hasStudents ? (
                                                <span style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: '0.9em', paddingLeft: '10px' }}>Add a class to enable tutorials</span>
                                            ) : existingHomeworks.length > 0 ? (
                                                existingHomeworks.map(hw => {
                                                    const stats = homeworkProgressStats[hw._id] || {};
                                                    return (
                                                    <div key={hw._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <Link to={homeworkRoute(hw._id)} style={{textDecoration:'none'}}>
                                                            <div data-guide="hs-tutorial-link" style={{
                                                                backgroundColor: '#E0F7FA', color: '#006064', border:`1px solid ${BRAND.teal}`,
                                                                padding: '8px 16px', borderRadius: '8px', fontWeight:'700', fontSize:'0.9em',
                                                                transition: 'all 0.2s', display:'flex', alignItems:'center', gap:'5px',
                                                                boxShadow: '2px 2px 0px rgba(117, 185, 190, 0.3)'
                                                            }}
                                                            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                                            >
                                                                Tutorial {hw.homeworkNumber} <span style={{fontSize:'1.2em'}}>→</span>
                                                            </div>
                                                        </Link>

                                                    </div>
                                                );})
                                            ) : (
                                                <span style={{ color: '#aaa', fontStyle: 'italic', fontSize:'0.9em', paddingLeft:'10px' }}>No active tutorials.</span>
                                            )}
                                        </div>

                                        {activeStudentsForLesson > 0 && (
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                background: '#DFF3E6',
                                                border: `1px solid ${BRAND.success}`,
                                                borderRadius: '8px',
                                                padding: '4px 10px',
                                                width: 'fit-content',
                                                fontSize: '0.8rem',
                                                fontWeight: '700',
                                                color: '#2F6D3A'
                                            }}>
                                                Active with students: {activeStudentsForLesson}
                                            </div>
                                        )}

                                        {existingHomeworks.length > 0 && (() => {
                                            const totals = existingHomeworks.reduce((acc, hw) => {
                                                const stats = homeworkProgressStats[hw._id] || {};
                                                const sent = Number(stats.sentCount) || 0;
                                                const completed = Number(stats.completedCount) || 0;
                                                const avgSub = Number(stats.avgSubstantiveCompleted) || 0;
                                                const avgKey = Number(stats.avgKeyTermsCompleted) || 0;

                                                acc.sent += sent;
                                                acc.completed += completed;
                                                acc.subWeighted += avgSub * completed;
                                                acc.keyWeighted += avgKey * completed;
                                                return acc;
                                            }, { sent: 0, completed: 0, subWeighted: 0, keyWeighted: 0 });

                                            const completedPct = totals.sent > 0 ? Math.round((totals.completed / totals.sent) * 100) : 0;
                                            const avgSubstantive = totals.completed > 0 ? Math.round(totals.subWeighted / totals.completed) : 0;
                                            const avgKeyTerms = totals.completed > 0 ? Math.round(totals.keyWeighted / totals.completed) : 0;

                                            if (totals.sent === 0) return null;

                                            return (
                                                <div data-guide="hs-tutorial-report" style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: '12px',
                                                    background: '#F8FAFC',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '10px',
                                                    padding: '8px 12px',
                                                    width: 'fit-content'
                                                }}>
                                                    <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: '700' }}>
                                                        Progress: <strong style={{ color: BRAND.charcoal }}>{completedPct}% completed</strong>
                                                    </span>
                                                    <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: '700' }}>
                                                        Substantive: <strong style={{ color: BRAND.charcoal }}>{avgSubstantive}%</strong>
                                                    </span>
                                                    <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: '700' }}>
                                                        Key Terms: <strong style={{ color: BRAND.charcoal }}>{avgKeyTerms}%</strong>
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {isOakAcademy && !hasStudents ? (
                                    <span style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: '0.85em', whiteSpace: 'nowrap' }}>Add class first</span>
                                ) : (
                                    <button
                                        className="btn-base btn-coral"
                                        data-guide="hs-create-tutorial"
                                        onClick={() => setModalState({ isOpen: true, lessonNumber: lessonNum })}
                                        disabled={!canCreateTutorial}
                                        title={canCreateTutorial ? '' : isOakAcademy ? 'Add a class first to enable tutorials.' : 'Generate this lesson on the Lesson Plan page before creating tutorials.'}
                                    >
                                        {canCreateTutorial ? '+ Create' : isOakAcademy ? 'Add Class First' : 'Generate Lesson First'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {showWilliamFordPrePostTest && (
                    <>
                    <h2
                        id="pre-post-test-heading"
                        style={{ margin: '30px 0 20px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontWeight: '800' }}
                    >
                        Pre/Post Test
                    </h2>
                    <section
                        className="dash-card"
                        aria-labelledby="pre-post-test-heading"
                        style={{ padding: '25px', marginBottom: '40px', background: 'white' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                            <span style={{ background: '#E0F7FA', color: '#006064', border: `1px solid ${BRAND.teal}`, borderRadius: '999px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: '800', whiteSpace: 'nowrap' }}>
                                William Ford only
                            </span>
                        </div>

                        {prePostWorksheetOptions.length === 0 ? (
                            <div style={{ border: '2px dashed #E0E0E0', borderRadius: '12px', padding: '22px', color: '#64748B', fontWeight: '700', background: '#F8FAFC' }}>
                                Lesson analysis is still loading or has not been generated yet. Generate lesson analysis first, then return here to print the pre/post worksheets.
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' }}>
                                {[
                                    {
                                        testType: 'Pre-Test',
                                        label: 'Pre-test worksheet',
                                        value: preTestLessonTag,
                                        onChange: setPreTestLessonTag,
                                        preview: preWorksheetPreview,
                                        generateLabel: 'Generate Pre-Test',
                                        buttonLabel: 'Print Pre-Test'
                                    },
                                    {
                                        testType: 'Post-Test',
                                        label: 'Post-test worksheet',
                                        value: postTestLessonTag,
                                        onChange: setPostTestLessonTag,
                                        preview: postWorksheetPreview,
                                        generateLabel: 'Generate Post-Test',
                                        buttonLabel: 'Print Post-Test'
                                    }
                                ].map(config => {
                                    const isGenerated = Boolean(generatedPrePostWorksheets[config.testType]);
                                    const isGenerating = prePostGeneratingType === config.testType;
                                    const typeError = prePostErrorsByType[config.testType] || '';

                                    return (
                                    <div key={config.testType} style={{ border: '2px solid #E0E0E0', borderRadius: '14px', padding: '16px', background: '#FCFCFC', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontWeight: '800' }}>
                                                {config.label}
                                            </label>
                                            <select
                                                className="dash-input"
                                                value={config.value}
                                                onChange={event => config.onChange(event.target.value)}
                                                aria-label={config.label}
                                            >
                                                {prePostWorksheetOptions.map(option => (
                                                    <option key={`${config.testType}-${option.value}`} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {config.preview && (
                                            <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '14px', color: BRAND.charcoal }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                                                    <div style={{ fontSize: '0.8rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '800' }}>
                                                        Worksheet preview
                                                    </div>
                                                    <span style={{ fontSize: '0.72rem', borderRadius: '999px', padding: '3px 8px', fontWeight: '800', background: isGenerated ? '#DFF3E6' : '#F1F5F9', color: isGenerated ? '#2F6D3A' : '#64748B' }}>
                                                        {isGenerated ? 'Generated' : 'Not generated'}
                                                    </span>
                                                </div>
                                                <div style={{ fontWeight: '800', fontSize: '1.05rem', marginBottom: '8px' }}>{config.preview.topic}</div>
                                                <div style={{ display: 'grid', gap: '6px', fontSize: '0.88rem', color: '#475569', fontWeight: '700' }}>
                                                    <span>Includes: {config.preview.lessonTopics.length} lessons in one worksheet</span>
                                                    <span>Page 1: Visual recognition</span>
                                                    <span>Page 2: Vocabulary — {config.preview.vocabTerms.join(' / ')}</span>
                                                    <span>Page 3: Sorting — {config.preview.sorting.leftLabel} / {config.preview.sorting.rightLabel}</span>
                                                    <span>Page 4: Scaffolded knowledge</span>
                                                </div>
                                            </div>
                                        )}

                                        {typeError && (
                                            <div style={{ border: `2px solid ${BRAND.danger}`, borderRadius: '10px', padding: '10px 14px', color: BRAND.danger, fontWeight: '700', background: '#FFF7F5', fontSize: '0.88rem' }}>
                                                {typeError}
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            {isGenerating && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 16px', border: '2px solid #E2E8F0', borderRadius: '10px', background: '#F8FAFC' }}>
                                                    <div style={{ width: '18px', height: '18px', border: '3px solid #E2E8F0', borderTopColor: BRAND.coral, borderRadius: '50%', animation: 'cc-spin 0.8s linear infinite', flexShrink: 0 }} />
                                                    <span style={{ fontWeight: '800', fontSize: '0.9rem', color: BRAND.charcoal }}>Generating worksheet…</span>
                                                </div>
                                            )}
                                            {!isGenerating && !isGenerated && (
                                                <button
                                                    className="btn-base btn-coral"
                                                    type="button"
                                                    onClick={() => handleGeneratePrePostWorksheet(config.testType)}
                                                    disabled={!config.preview}
                                                    style={{ opacity: config.preview ? 1 : 0.6 }}
                                                >
                                                    {typeError ? `Retry ${config.testType}` : config.generateLabel}
                                                </button>
                                            )}
                                            {!isGenerating && isGenerated && (
                                                <>
                                                    <button
                                                        className="btn-base btn-teal"
                                                        type="button"
                                                        onClick={() => handleOpenPrintModal(config.testType)}
                                                    >
                                                        Edit &amp; Print
                                                    </button>
                                                    <button
                                                        className="btn-base btn-coral"
                                                        type="button"
                                                        onClick={() => handleGeneratePrePostWorksheet(config.testType)}
                                                        disabled={!config.preview}
                                                    >
                                                        Regenerate
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                    </>
                )}

                <CreationModal
                    isOpen={modalState.isOpen}
                    onClose={() => setModalState({ isOpen: false, lessonNumber: null })}
                    onSubmit={handleCreateHomework}
                    lessonNumber={modalState.lessonNumber}
                    totalLessons={numberOfLessons}
                    students={students}
                    isSupplyTeacherMode={isSupplyTeacherMode}
                    profileGroups={profileGroups}
                />

                <StudentCreationModal
                    isOpen={isStudentModalOpen}
                    onClose={() => setIsStudentModalOpen(false)}
                    onSubmit={handleCreateStudent}
                    yearGroup={scheduleData?.yearGroup}
                    primaryLanguageOptions={primaryLanguageOptions}
                    languagesLoading={languagesLoading}
                    isSupplyTeacherMode={isSupplyTeacherMode}
                    readingAgeMode={localStorage.getItem(raKey) || 'default'}
                    currentStudentCount={students.length}
                />

                <ProfileCreationModal
                    isOpen={isProfileModalOpen}
                    onClose={() => setIsProfileModalOpen(false)}
                    onSubmit={handleAddProfile}
                    yearGroup={scheduleData?.yearGroup}
                    primaryLanguageOptions={primaryLanguageOptions}
                    languagesLoading={languagesLoading}
                    mode="create"
                />

                <ProfileCreationModal
                    isOpen={editProfileModal.isOpen}
                    onClose={() => setEditProfileModal({ isOpen: false, profile: null })}
                    onSubmit={handleEditProfile}
                    yearGroup={scheduleData?.yearGroup}
                    primaryLanguageOptions={primaryLanguageOptions}
                    languagesLoading={languagesLoading}
                    mode="edit"
                    initialProfile={editProfileModal.profile}
                />

                <StudentCreationModal
                    isOpen={editScaffoldingModal.isOpen}
                    onClose={() => setEditScaffoldingModal({ isOpen: false, student: null })}
                    onSubmit={(data) => handleUpdateStudentScaffolding(editScaffoldingModal.student?._id, data)}
                    yearGroup={scheduleData?.yearGroup}
                    primaryLanguageOptions={primaryLanguageOptions}
                    languagesLoading={languagesLoading}
                    mode="edit"
                    initialData={editScaffoldingModal.student}
                />

                <ExistingEntityModal
                    isOpen={isExistingStudentModalOpen}
                    onClose={() => setIsExistingStudentModalOpen(false)}
                    title="Add Existing Student"
                    items={studentLibrary}
                    loading={existingLibraryLoading}
                    emptyMessage="No previously created students available."
                    itemLabel={(student) => {
                        const className = student?.homeworkScheduleId?.name || 'Unknown class';
                        return (
                            <>
                                <div style={{ fontWeight: '700' }}>{student.name}</div>
                                <div style={{ fontSize: '0.85em', color: '#6B7280' }}>
                                    {className} • Age {student.age} • RA {student.readingAgeLabel || student.readingAge}
                                </div>
                            </>
                        );
                    }}
                    onConfirm={handleAddExistingStudent}
                    confirmLabel="Add Student"
                />

                <ExistingEntityModal
                    isOpen={isExistingClassModalOpen}
                    onClose={() => setIsExistingClassModalOpen(false)}
                    title="Add Existing Class"
                    items={classLibrary}
                    loading={existingLibraryLoading}
                    emptyMessage="No classes with students found yet."
                    itemLabel={(klass) => (
                        <>
                            <div style={{ fontWeight: '700' }}>{klass.name}</div>
                            <div style={{ fontSize: '0.85em', color: '#6B7280' }}>
                                {klass.subject} • {klass.yearGroup} • {klass.studentCount} students
                            </div>
                        </>
                    )}
                    onConfirm={handleAddExistingClass}
                    confirmLabel="Add Class"
                />
                {newClassModal.isOpen && (
                    <div onClick={() => !newClassModal.saving && setNewClassModal({ isOpen: false, value: '', saving: false, error: '' })} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45,64,66,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                        <div onClick={e => e.stopPropagation()} className="dash-card" style={{ padding: '28px', width: '420px', maxWidth: '94%', background: 'white' }}>
                            <h3 style={{ margin: '0 0 8px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif' }}>Create New Class</h3>
                            <p style={{ margin: '0 0 14px 0', fontSize: '0.88em', color: '#5D7375' }}>Create another class for this lesson plan, then manage its profiles separately.</p>
                            <input
                                className="dash-input"
                                type="text"
                                placeholder="Enter class name"
                                value={newClassModal.value}
                                onChange={e => setNewClassModal(prev => ({ ...prev, value: e.target.value, error: '' }))}
                                onKeyDown={e => e.key === 'Enter' && !newClassModal.saving && handleCreateNewClass()}
                                autoFocus
                            />
                            {newClassModal.error && <p style={{ color: BRAND.danger, fontSize: '0.85em', margin: '10px 0 0 0' }}>{newClassModal.error}</p>}
                            <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button className="btn-base" onClick={() => setNewClassModal({ isOpen: false, value: '', saving: false, error: '' })} style={{ background: '#F0F2F5', color: BRAND.charcoal }} disabled={newClassModal.saving}>Cancel</button>
                                <button className="btn-base btn-teal" onClick={handleCreateNewClass} disabled={newClassModal.saving}>
                                    {newClassModal.saving ? 'Creating...' : 'Create Class'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
        {classNameModal.isOpen && (
            <div onClick={() => !classNameModal.saving && setClassNameModal(prev => ({ ...prev, isOpen: false }))} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45,64,66,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <div onClick={e => e.stopPropagation()} className="dash-card" style={{ padding: '28px', width: '400px', maxWidth: '94%', background: 'white' }}>
                    <h3 style={{ margin: '0 0 8px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif' }}>Name Your Class</h3>
                    <p style={{ margin: '0 0 16px 0', fontSize: '0.88em', color: '#5D7375' }}>Give this class a name before adding students.</p>
                    <input
                        className="dash-input"
                        type="text"
                        placeholder="e.g. Period 3 Geography"
                        value={classNameModal.value}
                        onChange={e => setClassNameModal(prev => ({ ...prev, value: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && handleClassNameConfirm()}
                        autoFocus
                        style={{ width: '100%', marginBottom: '12px' }}
                    />
                    {classNameModal.error && <p style={{ color: BRAND.danger, fontSize: '0.85em', margin: '0 0 10px 0' }}>{classNameModal.error}</p>}
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <button className="btn-base" onClick={() => setClassNameModal(prev => ({ ...prev, isOpen: false }))} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Cancel</button>
                        <button className="btn-base btn-teal" onClick={handleClassNameConfirm} disabled={classNameModal.saving}>
                            {classNameModal.saving ? 'Saving...' : 'Confirm & Continue'}
                        </button>
                    </div>
                </div>
            </div>
        )}
        {guideConfig.isOpen && (
            <HomeworkScheduleGuideOverlay
                onClose={handleGuideClose}
                mode={guideConfig.mode}
                autoStep={guideConfig.autoStep}
                hasStudents={hasStudents}
                anonymousProfiles={isSupplyTeacherMode}
            />
        )}
        {printStudentModal.isOpen && (() => {
            const modalStudents = students.filter(s => s.name);
            const selectedSet = new Set(printStudentModal.selectedIds);
            const allSelected = modalStudents.length > 0 && modalStudents.every(s => selectedSet.has(s._id || s.id));
            const toggleStudent = (sid) => {
                const next = new Set(selectedSet);
                next.has(sid) ? next.delete(sid) : next.add(sid);
                setPrintStudentModal(prev => ({ ...prev, selectedIds: [...next] }));
            };
            const toggleAll = () => {
                const nextIds = allSelected ? [] : modalStudents.map(s => s._id || s.id).filter(Boolean);
                setPrintStudentModal(prev => ({ ...prev, selectedIds: nextIds }));
            };
            const selectedStudents = modalStudents.filter(s => selectedSet.has(s._id || s.id));
            return (
                <div onClick={() => setPrintStudentModal({ isOpen: false, testType: '', selectedIds: [] })} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45,64,66,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div onClick={e => e.stopPropagation()} className="dash-card" style={{ padding: '28px', width: '440px', maxWidth: '95%', background: 'white', maxHeight: '85vh', overflowY: 'auto' }}>
                        <h3 style={{ margin: '0 0 4px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif' }}>Print {printStudentModal.testType}</h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '0.85em', color: '#5D7375' }}>Select the students to print worksheets for.</p>
                        {modalStudents.length === 0 ? (
                            <p style={{ color: '#5D7375', fontSize: '0.9em' }}>No named students in this class. A single blank worksheet will be printed.</p>
                        ) : (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '0.85em', fontWeight: '700', color: '#5D7375' }}>{selectedStudents.length} of {modalStudents.length} selected</span>
                                    <button className="btn-base" onClick={toggleAll} style={{ background: '#F0F2F5', color: BRAND.charcoal, fontSize: '0.8rem', padding: '4px 12px' }}>
                                        {allSelected ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '340px', overflowY: 'auto', marginBottom: '16px' }}>
                                    {modalStudents.map(s => {
                                        const sid = s._id || s.id;
                                        return (
                                            <label key={sid} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', background: selectedSet.has(sid) ? '#F0FAF9' : '#F8FAFC', border: `1px solid ${selectedSet.has(sid) ? BRAND.teal : '#E2E8F0'}`, fontWeight: '700', fontSize: '0.9rem', color: BRAND.charcoal }}>
                                                <input type="checkbox" checked={selectedSet.has(sid)} onChange={() => toggleStudent(sid)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                                                {s.name}
                                            </label>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button className="btn-base" onClick={() => setPrintStudentModal({ isOpen: false, testType: '', selectedIds: [] })} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Cancel</button>
                            <button className="btn-base btn-teal" onClick={() => handlePrintPrePostWorksheet(printStudentModal.testType, selectedStudents.length > 0 ? selectedStudents : undefined)} disabled={modalStudents.length > 0 && selectedStudents.length === 0}>
                                Print {selectedStudents.length > 0 ? `${selectedStudents.length} worksheet${selectedStudents.length > 1 ? 's' : ''}` : 'worksheet'}
                            </button>
                        </div>
                    </div>
                </div>
            );
        })()}
        </>
    );
};

export default DemoHomeworkScheduleDetail;
